
import React, { useState, useRef, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';
import LoadingSpinner from '../utils/LoadingSpinner';
import URLManager from '../funcionalidadeNavbar/URLManager';
import './EncaixeManualDXF.css';
  

const SVG_SCALE_DEFAULT = 10;
const DEFAULT_CONFIG = {
  comprimentoMaterial: 1000,
  larguraMaterial: 1500,
  margemEsquerda: 10,
  margemDireita: 10,
  margemTopo: 10,
  margemBaixo: 10,
  distanciaPecas: 5,
  camadas: 2,
  aproximacaoAutomatica: true,
  giraAoAproximar: true,
  altaQualidade: false, // por padrão desmarcado na tela de Encaixe Manual
  permitirSobreposicao: true // permite girar/espelhar mesmo com sobreposição
};

// pixel threshold to snap to margins/edges
const SNAP_PX = 8;

// const SNAP_THRESHOLD_PX = 6; // distância máxima (em px) para "grudar" na margem (não utilizado)

function bboxOf(p) {
  if (!p) return { x: 0, y: 0, width: 0, height: 0 };
  
  // Se a peça tem entities array (estrutura de grupo), calcular bbox de todos
  if (p.entities && Array.isArray(p.entities) && p.entities.length > 0) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    
    p.entities.forEach((entity, idx) => {
      if (entity.points && entity.points.length) {
        entity.points.forEach(pt => {
          const x = Array.isArray(pt) ? pt[0] : (pt.x ?? pt[0]);
          const y = Array.isArray(pt) ? pt[1] : (pt.y ?? pt[1]);
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        });
      }
    });
    
    if (minX === Infinity) return { x: 0, y: 0, width: 0, height: 0 };
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }
  
  // Fallback para peças antigas (single entity)
  if (p.type === 'CIRCLE' && p.center && p.radius != null) {
    const cx = p.center[0], cy = p.center[1], r = p.radius;
    return { x: cx - r, y: cy - r, width: 2 * r, height: 2 * r };
  }
  if (p.type === 'RECTANGLE' && p.x != null && p.y != null && p.width != null && p.height != null) {
    return { x: p.x, y: p.y, width: p.width, height: p.height };
  }
  if (p.points && p.points.length) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    p.points.forEach(pt => {
      const x = Array.isArray(pt) ? pt[0] : (pt.x ?? pt[0]);
      const y = Array.isArray(pt) ? pt[1] : (pt.y ?? pt[1]);
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    });
    if (minX === Infinity) return { x: 0, y: 0, width: 0, height: 0 };
    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  }
  return { x: p.x || 0, y: p.y || 0, width: p.width || 0, height: p.height || 0 };
}

// Removido detectCollision (não utilizado) – usamos versões com espaçamento/exatas abaixo

// ==================== FUNÇÕES HELPER PARA THUMBNAILS ====================

// Calcula bounding box de uma peça baseado em suas entidades
const getPieceBBox = (entities) => {
  if (!entities || entities.length === 0) {
    return { minX: 0, minY: 0, width: 1, height: 1 };
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  entities.forEach(entity => {
    // POLYLINE / LWPOLYLINE / SPLINE: usar os pontos
    if (entity.points && entity.points.length > 0) {
      entity.points.forEach(pt => {
        const px = Array.isArray(pt) ? (pt[0] ?? 0) : (pt.x ?? 0);
        const py = Array.isArray(pt) ? (pt[1] ?? 0) : (pt.y ?? 0);
        if (px < minX) minX = px;
        if (py < minY) minY = py;
        if (px > maxX) maxX = px;
        if (py > maxY) maxY = py;
      });
      // ❌ REMOVIDO return - deve continuar processando outras entidades
    }
    // LINE com x1,y1,x2,y2
    else if (entity.x1 != null && entity.y1 != null && entity.x2 != null && entity.y2 != null) {
      const xs = [entity.x1, entity.x2];
      const ys = [entity.y1, entity.y2];
      minX = Math.min(minX, ...xs);
      minY = Math.min(minY, ...ys);
      maxX = Math.max(maxX, ...xs);
      maxY = Math.max(maxY, ...ys);
      // ❌ REMOVIDO return - deve continuar processando outras entidades
    }
    // CIRCLE com center/radius
    else if ((entity.center && (Array.isArray(entity.center) || typeof entity.center === 'object')) && entity.radius != null) {
      const cx = Array.isArray(entity.center) ? (entity.center[0] ?? 0) : (entity.center.x ?? 0);
      const cy = Array.isArray(entity.center) ? (entity.center[1] ?? 0) : (entity.center.y ?? 0);
      const r = entity.radius || 0;
      minX = Math.min(minX, cx - r);
      minY = Math.min(minY, cy - r);
      maxX = Math.max(maxX, cx + r);
      maxY = Math.max(maxY, cy + r);
      // ❌ REMOVIDO return - deve continuar processando outras entidades
    }
    // Fallback: usar x,y,width,height
    // Fallback: usar x,y,width,height
    else {
      const x = entity.x || 0;
      const y = entity.y || 0;
      const w = entity.width || 0;
      const h = entity.height || 0;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + w);
      maxY = Math.max(maxY, y + h);
    }
  });

  if (!isFinite(minX) || !isFinite(minY) || !isFinite(maxX) || !isFinite(maxY)) {
    return { minX: 0, minY: 0, width: 1, height: 1 };
  }

  return {
    minX,
    minY,
    width: Math.max(1, maxX - minX),
    height: Math.max(1, maxY - minY)
  };
};

// Renderiza uma entidade DXF como elemento SVG
const renderEntitySVG = (entity, index, strokeWidth = 0.5) => {
  const { type, x = 0, y = 0, width = 0, height = 0 } = entity;
  switch (type) {
    case 'POLYLINE':
    case 'LWPOLYLINE':
    case 'SPLINE': {
      const points = entity.points || [];
      if (points.length < 2) return null;
      let pathData = `M ${points[0][0]} ${points[0][1]}`;
      for (let i = 1; i < points.length; i++) {
        pathData += ` L ${points[i][0]} ${points[i][1]}`;
      }
      return (
        <path
          key={index}
          d={pathData}
          fill="none"
          stroke="#333"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          shapeRendering="geometricPrecision"
        />
      );
    }
    case 'LINE':
      if (entity.x1 != null && entity.y1 != null && entity.x2 != null && entity.y2 != null) {
        return (
          <line
            key={index}
            x1={entity.x1}
            y1={entity.y1}
            x2={entity.x2}
            y2={entity.y2}
            stroke="#333"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            shapeRendering="geometricPrecision"
          />
        );
      }
      return (
        <line
          key={index}
          x1={x}
          y1={y}
          x2={x + width}
          y2={y + height}
          stroke="#333"
          strokeWidth={strokeWidth}
        />
      );
    case 'CIRCLE':
      if ((entity.center && (Array.isArray(entity.center) || typeof entity.center === 'object')) && entity.radius != null) {
        const cx = Array.isArray(entity.center) ? (entity.center[0] ?? 0) : (entity.center.x ?? 0);
        const cy = Array.isArray(entity.center) ? (entity.center[1] ?? 0) : (entity.center.y ?? 0);
        return (
          <circle key={index} cx={cx} cy={cy} r={entity.radius || 0} fill="none" stroke="#333" strokeWidth={strokeWidth} shapeRendering="geometricPrecision" />
        );
      }
      {
        const radius = width / 2;
        return (
          <circle
            key={index}
            cx={x + radius}
            cy={y + radius}
            r={radius}
            fill="none"
            stroke="#333"
            strokeWidth={strokeWidth}
          />
        );
      }
    case 'ELLIPSE':
      return (
        <ellipse
          key={index}
          cx={x + width / 2}
          cy={y + height / 2}
          rx={width / 2}
          ry={height / 2}
          fill="none"
          stroke="#333"
          strokeWidth={strokeWidth}
        />
      );
    case 'ARC':
      // Simplificação: desenha como path (necessita start/end angle do backend)
      return (
        <circle
          key={index}
          cx={x + width / 2}
          cy={y + height / 2}
          r={width / 2}
          fill="none"
          stroke="#333"
          strokeWidth={strokeWidth}
        />
      );
    case 'RECTANGLE':
      return (
        <rect
          key={index}
          x={x}
          y={y}
          width={width}
          height={height}
          fill="none"
          stroke="#333"
          strokeWidth={strokeWidth}
        />
      );
    default:
      // Fallback para tipos não conhecidos
      return (
        <rect
          key={index}
          x={x}
          y={y}
          width={width || 1}
          height={height || 1}
          fill="none"
          stroke="#999"
          strokeWidth={strokeWidth}
          strokeDasharray="2,2"
        />
      );
  }
};

// ==================== COMPONENTE THUMBNAIL ITEM ====================

const ThumbnailItem = ({ peca, index, isSelected, onClick }) => {
  const bbox = getPieceBBox(peca.entities);
  
  // Se bbox muito pequeno, cria margem para evitar zoom extremo
  const pad = 5;
  const vx = bbox.minX - pad;
  const vy = bbox.minY - pad;
  const vw = bbox.width + pad * 2;
  const vh = bbox.height + pad * 2;
  const viewBox = `${vx} ${vy} ${vw} ${vh}`;
  const strokeWidth = Math.max(0.2, Math.min(bbox.width, bbox.height) / 200);

  return (
    <div
      onClick={() => onClick(index)}
      style={{
        width: '96px',
        height: '96px',
        border: isSelected ? '2px solid #1976d2' : '1px solid #ccc',
        borderRadius: '4px',
        padding: '4px',
        cursor: 'pointer',
        backgroundColor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '4px',
        transition: 'all 0.2s'
      }}
    >
      <svg
        width="80"
        height="80"
        viewBox={viewBox}
        preserveAspectRatio="xMidYMid meet"
        style={{ border: '1px solid #eee', background: '#fff' }}
      >
        {Array.isArray(peca.entities) && peca.entities.length > 0 ? (
          peca.entities.map((entity, idx) => renderEntitySVG(entity, idx, strokeWidth))
        ) : (
          <text x={vx + vw / 2} y={vy + vh / 2} textAnchor="middle" fontSize={12} fill="#999">(vazio)</text>
        )}
      </svg>
      <div style={{ fontSize: '10px', color: '#666', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
        {peca.name || peca.nome || `Peça ${index + 1}`}
      </div>
    </div>
  );
};

// ==================== COMPONENTE THUMBNAIL BAR ====================

const ThumbnailBar = ({ pecas, selectedIndex, onSelect }) => {
  if (!pecas || pecas.length === 0) return null;

  return (
    <div
      style={{
        display: 'flex',
        gap: '8px',
        padding: '12px',
        backgroundColor: '#fafafa',
        borderBottom: '1px solid #ddd',
        overflowX: 'auto',
        flexShrink: 0
      }}
    >
      {pecas.map((peca, index) => (
        <ThumbnailItem
          key={index}
          peca={peca}
          index={index}
          isSelected={index === selectedIndex}
          onClick={onSelect}
        />
      ))}
    </div>
  );
};

// ==================== COMPONENTE PRINCIPAL ====================

const EncaixeManualDXF = ({ dxfId, pecasDoDXF = [], pecaSelecionadaIndex = 0, onPecaSelect, onClose, onConsumoCalculado, preloadedDXFFile = null, numeroGrade = null, componente = null }) => {
  // Estado para armazenar as peças do DXF múltiplo e a peça atual selecionada
  const [pecasDisponiveis, setPecasDisponiveis] = useState(pecasDoDXF);
  const [indexAtual, setIndexAtual] = useState(pecaSelecionadaIndex);
  // UI/UX: Controles de layout compacto e colapsável
  const [showConfig, setShowConfig] = useState(true);
  const [showThumbnails, setShowThumbnails] = useState(true);
  const [compactUI, setCompactUI] = useState(true);
  
  // Estado para consumo calculado
  // Função para calcular área e perímetro via backend
  // Parâmetros opcionais
  const [tipoCalculo, setTipoCalculo] = useState('metroQuadrado');
  const [percentualPerda, setPercentualPerda] = useState('');
  const [quantidadePares, setQuantidadePares] = useState('');
  const [quantidadePeca, setQuantidadePeca] = useState('');
  const [consumoCalculado, setConsumoCalculado] = useState(null);
  // Modo Repet (retângulo do layout) e estados de desenho
  const [repetMode, setRepetMode] = useState(false);
  const [isDrawingRepet, setIsDrawingRepet] = useState(false);
  const [repetStart, setRepetStart] = useState(null); // {x,y} em coords SVG
  const [repetRect, setRepetRect] = useState(null); // {x0,y0,x1,y1} em coords SVG (px)

  const [, setRepetAnchorStart] = useState(null); // { pieceIndex, side: 'left'|'right'|'top'|'bottom', t: 0..1 }
  
  // ========== Estados para seleção por arrasto ==========
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionBox, setSelectionBox] = useState(null);
  const [justFinishedSelection, setJustFinishedSelection] = useState(false);
  
  // ========== Estados para Modal de Consumo ==========
  const [modalConsumoOpen, setModalConsumoOpen] = useState(false);
  const [modalConsumoData, setModalConsumoData] = useState(null);
  
  // Passo de rotação via scroll (em graus)
  const [rotationStep, setRotationStep] = useState(1);
  // Estado para highlight da quina mais próxima
  const [snapHighlight, setSnapHighlight] = useState(null); // {x, y} ou null

  const handleCalcularConsumoManual = async () => {
    // Detecta se estamos usando o retângulo Repet (override largura/altura)
    const usandoRetanguloRepet = repetMode && repetRect && typeof repetRect.x0 === 'number' && typeof repetRect.x1 === 'number' && typeof repetRect.y0 === 'number' && typeof repetRect.y1 === 'number';
    // Ajuste: permitir cálculo com peças já carregadas (pieces) mesmo sem dxfFile, desde que haja seleção
    if (!usandoRetanguloRepet) {
      if (!dxfFile && selectedPieces.size === 0) {
        alert('Selecione pelo menos uma peça, ou ative/desenhe o retângulo Repet, ou carregue o arquivo DXF.');
        return;
      }
    }
    console.log('[ConsumoManual] Iniciando cálculo', { usandoRetanguloRepet, temDXF: !!dxfFile, selecionadas: selectedPieces.size });

    const captureLayoutSnapshotPng = async () => {
      try {
        const svgEl = svgRef.current;
        if (!svgEl) return null;

        // Calcular o viewBox que engloba todas as peças, sempre partindo de 0,0
        let vbToCapture = viewBox || '0 0 1000 1000';
        
        if (pieces && pieces.length > 0) {
          let maxX = -Infinity, maxY = -Infinity;
          
          pieces.forEach(p => {
            const bb = bboxOf(p);
            // Considerar x e y da peça (translate) + bbox interno
            const pieceX = (p.x || 0) + bb.x;
            const pieceY = (p.y || 0) + bb.y;
            maxX = Math.max(maxX, pieceX + bb.width);
            maxY = Math.max(maxY, pieceY + bb.height);
          });
          
          if (maxX !== -Infinity) {
            const margin = 40;
            // Sempre capturar desde 0,0 até o máximo + margem
            const vbW = Math.ceil(maxX + margin);
            const vbH = Math.ceil(maxY + margin);
            vbToCapture = `0 0 ${vbW} ${vbH}`;
            console.log('[EncaixeSnapshotDebug] ViewBox calculado:', vbToCapture, 'maxX:', maxX, 'maxY:', maxY);
          }
        }

        const vbParts = vbToCapture.split(/\s+/).map(Number);
        const width = Math.max(1, Math.round(vbParts[2] || 1000));
        const height = Math.max(1, Math.round(vbParts[3] || 1000));

        console.log('[EncaixeSnapshotDebug] Canvas size:', width, 'x', height, 'pieces:', pieces?.length);

        const clonedSvg = svgEl.cloneNode(true);
        clonedSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        clonedSvg.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
        clonedSvg.setAttribute('viewBox', vbToCapture);
        clonedSvg.setAttribute('width', String(width));
        clonedSvg.setAttribute('height', String(height));

        const serialized = new XMLSerializer().serializeToString(clonedSvg);
        const blob = new Blob([serialized], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(blob);

        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = url;
        });

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          URL.revokeObjectURL(url);
          return null;
        }

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        const pngDataUrl = canvas.toDataURL('image/png');
        URL.revokeObjectURL(url);
        
        console.log('[EncaixeSnapshotDebug] Snapshot capturado com sucesso, dataURL length:', pngDataUrl?.length);
        return pngDataUrl;
      } catch (e) {
        console.warn('[EncaixeManualDXF] Snapshot do encaixe não pôde ser gerado:', e);
        return null;
      }
    };

    try {
      const fd = new FormData();
      if (dxfFile) fd.append('file', dxfFile);
      // selected_indices vazio se usando retângulo manual
      if (usandoRetanguloRepet) {
        fd.append('selected_indices', JSON.stringify([]));
      } else {
        fd.append('selected_indices', JSON.stringify(Array.from(selectedPieces)));
      }
      fd.append('tipoCalculo', tipoCalculo);
      if (percentualPerda !== undefined && percentualPerda !== '') fd.append('percentualPerda', percentualPerda);
      if (quantidadePares !== undefined && quantidadePares !== '') fd.append('quantidadePares', quantidadePares);
      if (quantidadePeca !== undefined && quantidadePeca !== '') fd.append('quantidadePeca', quantidadePeca);
      // Override por largura/altura em cm quando modo Repet ativo e cálculo em metroQuadrado
      if (usandoRetanguloRepet && tipoCalculo === 'metroQuadrado') {
        const larguraPx = Math.abs(repetRect.x1 - repetRect.x0);
        const alturaPx = Math.abs(repetRect.y1 - repetRect.y0);
        const larguraCm = (larguraPx / svgScale) / 10; // px -> mm -> cm
        const alturaCm = (alturaPx / svgScale) / 10;
        fd.append('larguraLayoutCm', larguraCm.toFixed(4));
        fd.append('alturaLayoutCm', alturaCm.toFixed(4));
      }

      // Se a peça atual tiver um "nome" (ex.: layer), envia para o backend ajudar no filtro
      try {
        const pieceName = (pecasDisponiveis && pecasDisponiveis[indexAtual] && (pecasDisponiveis[indexAtual].name || pecasDisponiveis[indexAtual].nome)) || null;
        if (pieceName) fd.append('pieceName', pieceName);
        if (Number.isInteger(indexAtual)) fd.append('pieceIndex', String(indexAtual));
        // Include grade number and component context when provided
        if (numeroGrade !== undefined && numeroGrade !== null) fd.append('numeroGrade', String(numeroGrade));
        if (componente !== undefined && componente !== null) fd.append('componente', String(componente));
      } catch {}

      const JAVA_API_BASE = URLManager.getBaseURL();
      const res = await fetch(`${JAVA_API_BASE}/api/dxf/calcular-consumo-manual`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('usuarioToken')}`
        },
        body: fd
      });
      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        console.error('[ConsumoManual] Falha', res.status, errText);
        throw new Error('Erro ao calcular consumo');
      }
      const result = await res.json();
      
      // Preparar dados para o modal (reutilizar a variável já definida no início da função)
      setModalConsumoData({
        consumo: result.consumo_min_rotated_rect ?? result.consumo ?? '-',
        perimetro: result.perimetro_min_rotated_rect ?? result.perimetro ?? '-',
        usandoRepet: usandoRetanguloRepet
      });
      setModalConsumoOpen(true);
      
      setConsumoCalculado(result);
      if (typeof onConsumoCalculado === 'function') {
        // Try to extract a numeric consumption from multiple possible sources
        let consumoValor = result?.consumo_min_rotated_rect ?? result?.consumo ?? consumoCalculado?.consumo_min_rotated_rect ?? consumoCalculado?.consumo ?? null;
        let consumoFonte = null;
        if (consumoValor != null) {
          consumoFonte = 'backend';
        } else if (pecasDisponiveis && Number.isInteger(indexAtual) && pecasDisponiveis[indexAtual] && pecasDisponiveis[indexAtual].area) {
          consumoValor = pecasDisponiveis[indexAtual].area; // area is in mm² in our model
          consumoFonte = 'pecasDisponiveis[indexAtual].area';
        } else if (layoutResult && (layoutResult.consumo_min_rotated_rect || layoutResult.consumo)) {
          consumoValor = layoutResult.consumo_min_rotated_rect ?? layoutResult.consumo;
          consumoFonte = 'layoutResult';
        }

        const snapshotPng = await captureLayoutSnapshotPng();
        const resultWithContext = {
          ...(result || {}),
          numeroGrade: numeroGrade || null,
          componente: componente || null,
          encaixeSnapshotPng: snapshotPng,
          encaixeSnapshotAt: new Date().toISOString()
        };
        if (consumoValor != null) {
          // normalize to number when possible
          const numeric = typeof consumoValor === 'string' ? Number(consumoValor) : consumoValor;
          resultWithContext.consumo = numeric;
          resultWithContext.consumo_min_rotated_rect = numeric;
          console.debug('[EncaixeManualDXF] onConsumoCalculado - fornecendo consumo derivado', { consumo: numeric, fonte: consumoFonte });
        } else {
          console.warn('[EncaixeManualDXF] onConsumoCalculado - não encontrou consumo numérico; enviando contexto sem consumo', { result });
        }

        onConsumoCalculado(resultWithContext);
      }
    } catch (err) {
      console.error('Erro ao calcular consumo', err);
      alert('Erro ao calcular consumo');
    }
  };
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [svgScale] = useState(SVG_SCALE_DEFAULT);
  const [spacing, setSpacing] = useState(DEFAULT_CONFIG.distanciaPecas);
  const [pieces, setPieces] = useState([]);
  const [selected, setSelected] = useState(null);
  // Seleção múltipla (Ctrl/Cmd + clique)
  const [selectedPieces, setSelectedPieces] = useState(new Set());
  const [dragging, setDragging] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  // ViewBox default: largura no eixo X (width) e comprimento no eixo Y (height)
  const [viewBox, setViewBox] = useState(`0 0 ${DEFAULT_CONFIG.larguraMaterial * SVG_SCALE_DEFAULT} ${DEFAULT_CONFIG.comprimentoMaterial * SVG_SCALE_DEFAULT}`);
  // Removido estado de zoom não utilizado (mantemos zoom via viewBox)
  const [dxfFile, setDxfFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [layoutResult, setLayoutResult] = useState(null);
  const [fileiraVertical, setFileiraVertical] = useState([]); // State para fileira (horizontal ou vertical)
  // Histórico para desfazer (Ctrl+Z) – focado em movimentos
  const MAX_HISTORY = 50;
  const [history, setHistory] = useState([]);

  // Se receber arquivo DXF do pai (já carregado anteriormente), usa-o
  useEffect(() => {
    if (!dxfFile && preloadedDXFFile) {
      try { console.log('[DXF] Usando arquivo DXF pré-carregado do pai'); } catch {}
      setDxfFile(preloadedDXFFile);
    }
  }, [preloadedDXFFile, dxfFile]);

  const deepClonePieces = useCallback((arr) => {
    // Clonagem suficiente para nosso formato de peças
    return JSON.parse(JSON.stringify(arr));
  }, []);

  const svgRef = useRef(null);
  const moveRafRef = useRef({ id: 0, scheduled: false, x: 0, y: 0 });
  const dragStartRef = useRef(null); // { mouseX, mouseY, originals: { idx: pieceCopy } }
  const fastCollisionRef = useRef(true); // use bbox-based collision while dragging for responsiveness
  // Temporary spacing override (pixels). When not null, this value is used instead of config spacing.
  // We set this to 0 when the user holds Alt while dragging or using arrow keys to allow tight fitting.
  const spacingOverrideRef = useRef(null);
  // Espaçamento em mm convertido para pixels (SVG) com memoização
  const distanciaPecas = config ? config.distanciaPecas : 0; // evitar referenciar 'config' dentro do hook
  const getSpacingPx = useCallback(() => {
    const spacingMm = Number(distanciaPecas || 0);
    // If an override is present (e.g. user holds Alt), use it (already in pixels)
    if (spacingOverrideRef.current != null) return spacingOverrideRef.current;
    return spacingMm * svgScale; // Convertendo mm para pixels SVG
  }, [distanciaPecas, svgScale]);
  
  // Panning/Zooming helpers
  const [isPanning, setIsPanning] = useState(false);
  const panRef = useRef({ startX: 0, startY: 0, vb: { x: 0, y: 0, w: 0, h: 0 } });

  const normalizeCoordinates = useCallback((entities) => {
    if (!entities || !Array.isArray(entities)) return [];

    const toDeg = (rad) => (rad * 180) / Math.PI;
    const toRad = (deg) => (deg * Math.PI) / 180;

    const approximateArc = (center, radius, startAngle, endAngle, segments = 36) => {
      const pts = [];
      const sa = startAngle;
      const ea = endAngle < sa ? endAngle + 360 : endAngle;
      const span = Math.max(5, ea - sa);
      const steps = Math.min(segments, Math.max(6, Math.ceil((span / 360) * segments)));
      for (let i = 0; i <= steps; i++) {
        const ang = toRad(sa + ((ea - sa) * (i / steps)));
        pts.push([
          center[0] + radius * Math.cos(ang),
          center[1] + radius * Math.sin(ang)
        ]);
      }
      return pts;
    };

    const approximateEllipse = (center, rx, ry, startAngle = 0, endAngle = 360, rotationDeg = 0, segments = 60) => {
      const pts = [];
      const rot = toRad(rotationDeg || 0);
      const sa = startAngle;
      const ea = endAngle < sa ? endAngle + 360 : endAngle;
      const steps = Math.min(segments, Math.max(12, Math.ceil(((ea - sa) / 360) * segments)));
      for (let i = 0; i <= steps; i++) {
        const ang = toRad(sa + ((ea - sa) * (i / steps)));
        const xLocal = rx * Math.cos(ang);
        const yLocal = ry * Math.sin(ang);
        const xr = center[0] + xLocal * Math.cos(rot) - yLocal * Math.sin(rot);
        const yr = center[1] + xLocal * Math.sin(rot) + yLocal * Math.cos(rot);
        pts.push([xr, yr]);
      }
      return pts;
    };

    const norm = entities.map((raw, idx) => {
      if (!raw) return null;
      const ent = { ...raw };
      const type = String(ent.type || ent.entityType || '').toUpperCase();

      // 🔍 Debug primeira entidade para verificar formato Python
      if (idx === 0 && process.env.NODE_ENV === 'development') {
        console.log('🔍 Primeira entidade recebida para normalizar:', JSON.stringify(ent, null, 2).substring(0, 300));
      }

      // Corel / common DXF variations
      if (type === 'CIRCLE' && ent.center && ent.radius != null) {
        const center = [ent.center[0] * svgScale, ent.center[1] * svgScale];
        const radius = ent.radius * svgScale;
        return {
          type: 'CIRCLE',
          center,
          radius,
          x: center[0] - radius,
          y: center[1] - radius,
          width: 2 * radius,
          height: 2 * radius,
          rotation: 0
        };
      }

      if (type === 'RECTANGLE') {
        const x = (ent.x || 0) * svgScale;
        const y = (ent.y || 0) * svgScale;
        const w = (ent.width || 0) * svgScale;
        const h = (ent.height || 0) * svgScale;
        return { type: 'RECTANGLE', x, y, width: w, height: h, rotation: ent.rotation || 0 };
      }

      // LINE variants
      if (type === 'LINE' || (ent.x1 != null && ent.y1 != null && ent.x2 != null && ent.y2 != null) || (ent.start && ent.end)) {
        const sx = ent.x1 != null ? ent.x1 : ent.start?.[0];
        const sy = ent.y1 != null ? ent.y1 : ent.start?.[1];
        const ex = ent.x2 != null ? ent.x2 : ent.end?.[0];
        const ey = ent.y2 != null ? ent.y2 : ent.end?.[1];
        if (sx != null && sy != null && ex != null && ey != null) {
          const points = [[sx * svgScale, sy * svgScale], [ex * svgScale, ey * svgScale]];
          const bb = bboxOf({ points });
          return { type: 'POLYLINE', points, x: bb.x, y: bb.y, width: bb.width, height: bb.height, rotation: 0 };
        }
      }

      // LWPOLYLINE / POLYLINE with vertices
      if ((type === 'LWPOLYLINE' || type === 'POLYLINE') && Array.isArray(ent.vertices)) {
        const points = ent.vertices.map(v => [v[0] * svgScale, v[1] * svgScale]);
        const bb = bboxOf({ points });
        return { type: type, points, x: bb.x, y: bb.y, width: bb.width, height: bb.height, rotation: 0 };
      }

      // Any object containing 'points'
      if (Array.isArray(ent.points) && ent.points.length) {
        const points = ent.points.map(p => Array.isArray(p) ? [p[0] * svgScale, p[1] * svgScale] : [p.x * svgScale, p.y * svgScale]);
        const bb = bboxOf({ points });
        return { type: type || 'POLYLINE', points, x: bb.x, y: bb.y, width: bb.width, height: bb.height, rotation: ent.rotation || 0 };
      }

      // SPLINE (approximate using control points)
      if (type === 'SPLINE' && (ent.controlPoints || ent.control_points)) {
        const cps = (ent.controlPoints || ent.control_points) || [];
        const points = cps.map(p => Array.isArray(p) ? [p[0] * svgScale, p[1] * svgScale] : [p.x * svgScale, p.y * svgScale]);
        const bb = bboxOf({ points });
        return { type: 'SPLINE', points, x: bb.x, y: bb.y, width: bb.width, height: bb.height, rotation: 0 };
      }

      // ARC
      if (type === 'ARC' && ent.center && ent.radius != null) {
        const center = [ent.center[0] * svgScale, ent.center[1] * svgScale];
        const r = ent.radius * svgScale;
        const sa = ent.startAngle != null ? (ent.startAngle) : (ent.start_angle || 0);
        const ea = ent.endAngle != null ? (ent.endAngle) : (ent.end_angle || 360);
        const pts = approximateArc(center, r, sa, ea, 360);
        const bb = bboxOf({ points: pts });
        return { type: 'ARC', points: pts, x: bb.x, y: bb.y, width: bb.width, height: bb.height, rotation: 0 };
      }

      // ELLIPSE
      if (type === 'ELLIPSE' && ent.center && ent.majorAxis) {
        // Some parsers provide: center [x,y], majorAxis [dx,dy], axisRatio, startAngle, endAngle
        const center = [ent.center[0] * svgScale, ent.center[1] * svgScale];
        const major = ent.majorAxis; // vector
        const mx = major[0]; const my = major[1];
        const majorLen = Math.sqrt(mx * mx + my * my);
        const rx = majorLen * svgScale;
        const ry = rx * (ent.axisRatio != null ? ent.axisRatio : 1);
        const rotDeg = toDeg(Math.atan2(my, mx));
        const sa = ent.startAngle != null ? ent.startAngle : 0;
        const ea = ent.endAngle != null ? ent.endAngle : 360;
        const pts = approximateEllipse(center, rx, ry, sa, ea, rotDeg, 360);
        const bb = bboxOf({ points: pts });
        return { type: 'ELLIPSE', points: pts, x: bb.x, y: bb.y, width: bb.width, height: bb.height, rotation: 0 };
      }

      // Generic start/end arrays (fallback)
      if (ent.start && ent.end) {
        const points = [[ent.start[0] * svgScale, ent.start[1] * svgScale], [ent.end[0] * svgScale, ent.end[1] * svgScale]];
        const bb = bboxOf({ points });
        return { type: 'SEGMENT', points, x: bb.x, y: bb.y, width: bb.width, height: bb.height, rotation: 0 };
      }

      return null; // unrecognized
    }).filter(Boolean);

    // Preservar escala original do DXF sem auto-rescale
    // O auto-rescale estava distorcendo as dimensões originais das peças

    if (!norm.length) {
      console.warn('❌ DXF: Nenhuma entidade reconhecida após normalização.');
      console.warn('Primeiras 2 entidades recebidas:', entities.slice(0, 2));
      console.warn('Verifique se o export do Corel/AutoCAD está em formato R14/R12 com entidades básicas (LINES/POLYLINE).');
    } else if (process.env.NODE_ENV === 'development') {
      console.log('✅ DXF normalizado:', norm.length, 'entidades');
      console.log('Amostra (primeiras 2):', norm.slice(0, 2).map(n => ({ type: n.type, points: n.points?.length || 0, x: n.x, y: n.y })));
    }
    return norm;
  }, [svgScale]);

  // Helper functions for point extraction and rotation (used for moving and rotating pieces)
  const getPiecePoints = useCallback((piece) => {
    if (!piece) return [];
    if (piece.type === 'CIRCLE' && piece.center && piece.radius != null) {
      const cx = piece.center[0]; const cy = piece.center[1]; const r = piece.radius;
      return [[cx - r, cy - r], [cx + r, cy - r], [cx + r, cy + r], [cx - r, cy + r]];
    }
    if (piece.points && piece.points.length) return piece.points.map(pt => Array.isArray(pt) ? [pt[0], pt[1]] : [pt.x, pt.y]);
    if (piece.control_points && piece.control_points.length) return piece.control_points.map(pt => Array.isArray(pt) ? [pt[0], pt[1]] : [pt.x, pt.y]);
    const bb = bboxOf(piece);
    return [[bb.x, bb.y], [bb.x + bb.width, bb.y], [bb.x + bb.width, bb.y + bb.height], [bb.x, bb.y + bb.height]];
  }, []);

  const rotatePoint = useCallback((px, py, cx, cy, angleRad) => {
    const dx = px - cx, dy = py - cy;
    const rx = dx * Math.cos(angleRad) - dy * Math.sin(angleRad) + cx;
    const ry = dx * Math.sin(angleRad) + dy * Math.cos(angleRad) + cy;
    return [rx, ry];
  }, []);

  const rotatePoints = useCallback((pts, cx, cy, angleDeg) => {
    const a = (angleDeg || 0) * Math.PI / 180;
    return (pts || []).map(([x, y]) => rotatePoint(x, y, cx, cy, a));
  }, [rotatePoint]);

  const getRotationCenter = useCallback((piece) => {
    const bb = bboxOf(piece);
    return [bb.x + bb.width / 2, bb.y + bb.height / 2];
  }, []);



// Função utilitária fora do componente para evitar ciclo de hooks
const rotatedBBoxFromPiece = useCallback((piece, getPiecePoints, getRotationCenter, rotatePoints, getTransformedPoints) => {
  // Para peças com entities, usar getTransformedPoints que aplica rotação corretamente
  if (piece.entities && Array.isArray(piece.entities) && piece.entities.length > 0) {
    const transformedPts = getTransformedPoints(piece);
    if (transformedPts.length > 0) {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      transformedPts.forEach(([x, y]) => {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      });
      return { x: minX, y: minY, width: Math.max(0, maxX - minX), height: Math.max(0, maxY - minY) };
    }
  }
  // Para peças antigas (fallback)
  const pts = getPiecePoints(piece);
  const angle = Number(piece.rotation || 0) || 0;
  if (!angle) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    pts.forEach(([x, y]) => { if (x < minX) minX = x; if (y < minY) minY = y; if (x > maxX) maxX = x; if (y > maxY) maxY = y; });
    return { x: minX, y: minY, width: Math.max(0, maxX - minX), height: Math.max(0, maxY - minY) };
  }
  const [cx, cy] = getRotationCenter(piece);
  const ptsR = rotatePoints(pts, cx, cy, angle);
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  ptsR.forEach(([x, y]) => { if (x < minX) minX = x; if (y < minY) minY = y; if (x > maxX) maxX = x; if (y > maxY) maxY = y; });
  return { x: minX, y: minY, width: Math.max(0, maxX - minX), height: Math.max(0, maxY - minY) };
}, []);


  // Move getRenderPoints above getTransformedPoints
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getRenderPoints = useCallback((piece) => {
    if (!piece) return [];
    if (piece.entities && Array.isArray(piece.entities) && piece.entities.length > 0) {
      let largestEntity = null;
      let largestArea = 0;
      piece.entities.forEach((entity, idx) => {
        if (entity.points && entity.points.length) {
          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
          entity.points.forEach(pt => {
            const x = Array.isArray(pt) ? pt[0] : (pt.x ?? pt[0]);
            const y = Array.isArray(pt) ? pt[1] : (pt.y ?? pt[1]);
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x > maxX) maxX = x;
            if (y > maxY) maxY = y;
          });
          const width = maxX - minX;
          const height = maxY - minY;
          const area = width * height;
          if (area > largestArea) {
            largestArea = area;
            largestEntity = entity;
          }
        }
      });
      if (largestEntity && largestEntity.points && largestEntity.points.length) {
        const base = largestEntity.points.map(pt => Array.isArray(pt) ? [pt[0], pt[1]] : [pt.x, pt.y]);
        return base;
      }
    }
    if (piece.type === 'CIRCLE' && piece.center && piece.radius != null) {
      const pts = [];
      const cx = piece.center[0];
      const cy = piece.center[1];
      const r = piece.radius;
      const segments = 360;
      for (let i = 0; i < segments; i++) {
        const angle = (i / segments) * 2 * Math.PI;
        pts.push([cx + r * Math.cos(angle), cy + r * Math.sin(angle)]);
      }
      return pts;
    }
    if (piece.points && piece.points.length) {
      const base = piece.points.map(pt => Array.isArray(pt) ? [pt[0], pt[1]] : [pt.x, pt.y]);
      let transformed = base;
      if (piece.rotation && piece.rotation !== 0) {
        const [cx, cy] = getRotationCenter(piece);
        const a = (piece.rotation * Math.PI) / 180;
        transformed = base.map(([x, y]) => rotatePoint(x, y, cx, cy, a));
      }
      return transformed;
    }
    if (piece.type === 'RECTANGLE' && piece.width != null && piece.height != null) {
      const rectPts = [
        [piece.x, piece.y],
        [piece.x + piece.width, piece.y],
        [piece.x + piece.width, piece.y + piece.height],
        [piece.x, piece.y + piece.height]
      ];
      if (piece.rotation && piece.rotation !== 0) {
        const [cx, cy] = getRotationCenter(piece);
        const a = (piece.rotation * Math.PI) / 180;
        return rectPts.map(([x, y]) => rotatePoint(x, y, cx, cy, a));
      }
      return rectPts;
    }
    // No fallback: if no points found, do not return anything
  }, [rotatePoint, getRotationCenter]);

  // Now getTransformedPoints can use getRenderPoints safely
  const getTransformedPoints = useCallback((piece) => {
    const basePoints = getRenderPoints ? getRenderPoints(piece) : [];
    if (!basePoints.length) return [];
    if (piece.entities && Array.isArray(piece.entities) && piece.entities.length > 0) {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      piece.entities.forEach(entity => {
        if (entity.points && entity.points.length) {
          entity.points.forEach(pt => {
            const x = Array.isArray(pt) ? pt[0] : (pt.x ?? pt[0]);
            const y = Array.isArray(pt) ? pt[1] : (pt.y ?? pt[1]);
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x > maxX) maxX = x;
            if (y > maxY) maxY = y;
          });
        }
      });
      const localWidth = maxX - minX;
      const localHeight = maxY - minY;
      const centerX = minX + localWidth / 2;
      const centerY = minY + localHeight / 2;
      let transformed = basePoints;
      if (piece.rotation && piece.rotation !== 0) {
        const a = (piece.rotation * Math.PI) / 180;
        transformed = basePoints.map(([x, y]) => 
          rotatePoint(x, y, centerX, centerY, a)
        );
      }
      const offsetX = piece.x || 0;
      const offsetY = piece.y || 0;
      transformed = transformed.map(([x, y]) => [x + offsetX, y + offsetY]);
      return transformed;
    }
    return basePoints;
  }, [rotatePoint, getRenderPoints]);

  // Now getTransformedPoints can use getRenderPoints safely

  // Obter dimensões REAIS da peça (baseado nos pontos de renderização transformados)
  const getRealDimensions = useCallback((piece) => {
    const pts = getTransformedPoints(piece);
    if (!pts || !pts.length) {
      const bbox = rotatedBBoxFromPiece(piece, getPiecePoints, getRotationCenter, rotatePoints, getTransformedPoints);
      return {
        width: bbox.width,
        height: bbox.height,
        minX: bbox.x,
        minY: bbox.y,
        maxX: bbox.x + bbox.width,
        maxY: bbox.y + bbox.height
      };
    }
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const [x, y] of pts) {
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
    return { width: maxX - minX, height: maxY - minY, minX, minY, maxX, maxY };
  }, [getPiecePoints, getRotationCenter, getTransformedPoints, rotatePoints, rotatedBBoxFromPiece]);

  // Helpers to compute material pixel size and inner bounds (margins)
  // Mapear corretamente: Largura (mm) => eixo X (px), Comprimento (mm) => eixo Y (px)
  const materialPixelWidth = useCallback(() => (Number(config.larguraMaterial || 0) * Number(svgScale || 1)), [config.larguraMaterial, svgScale]);
  const materialPixelHeight = useCallback(() => (Number(config.comprimentoMaterial || 0) * Number(svgScale || 1)), [config.comprimentoMaterial, svgScale]);

  const getInnerBounds = useCallback(() => {
    const matW = materialPixelWidth();
    const matH = materialPixelHeight();
    const left = (Number(config.margemEsquerda || 0)) * svgScale;
    const right = (Number(config.margemDireita || 0)) * svgScale;
    const top = (Number(config.margemTopo || 0)) * svgScale;
    const bottom = (Number(config.margemBaixo || 0)) * svgScale;
    return { xMin: left, yMin: top, xMax: Math.max(left, matW - right), yMax: Math.max(top, matH - bottom) };
  }, [config, materialPixelWidth, materialPixelHeight, svgScale]);
  // Move helpers
  const deltaMovePiece = useCallback((p, dx, dy) => {
    const copy = { ...p };
    if (copy.points && copy.points.length) {
      copy.points = copy.points.map(pt => Array.isArray(pt) ? [pt[0] + dx, pt[1] + dy] : { x: pt.x + dx, y: pt.y + dy });
      const bb = bboxOf({ points: copy.points }); copy.x = bb.x; copy.y = bb.y; copy.width = bb.width; copy.height = bb.height;
    } else if (copy.center && copy.radius != null) {
      copy.center = [ (copy.center[0] || 0) + dx, (copy.center[1] || 0) + dy ];
      copy.x = copy.center[0] - copy.radius; copy.y = copy.center[1] - copy.radius;
    } else {
      copy.x = (copy.x || 0) + dx; copy.y = (copy.y || 0) + dy;
    }
    return copy;
  }, []);

  const movePieceTo = useCallback((p, x, y) => {
    const bb = bboxOf(p);
    const dx = (x - (bb.x || 0));
    const dy = (y - (bb.y || 0));
    return deltaMovePiece(p, dx, dy);
  }, [deltaMovePiece]);

  // Mover peça para que seu contorno REAL (minX/minY) encoste exatamente nas margens/posições alvo
  const movePieceRealMinTo = useCallback((p, targetMinX, targetMinY) => {
    const real = getRealDimensions(p);
    const dx = targetMinX - real.minX;
    const dy = targetMinY - real.minY;
    return deltaMovePiece(p, dx, dy);
  }, [getRealDimensions, deltaMovePiece]);

  // Mover peça para que seu contorno REAL (maxX/maxY) encoste exatamente nas posições alvo
  const movePieceRealMaxTo = useCallback((p, targetMaxX, targetMaxY) => {
    const real = getRealDimensions(p);
    const dx = targetMaxX - real.maxX;
    const dy = targetMaxY - real.maxY;
    return deltaMovePiece(p, dx, dy);
  }, [getRealDimensions, deltaMovePiece]);

  // Clamping à área útil usando contorno REAL (encosta na margem, não na borda do material)
  const clampPieceToMaterial = useCallback((p) => {
    if (!p) return p;
    const copy = { ...p };
    const inner = getInnerBounds();
    let real = getRealDimensions(copy);

    // Se estiver completamente fora, centraliza dentro da área útil
    const isCompletelyOutside =
      (real.maxX < inner.xMin) ||
      (real.minX > inner.xMax) ||
      (real.maxY < inner.yMin) ||
      (real.minY > inner.yMax);

    if (isCompletelyOutside) {
      const realW = Math.max(1, real.width);
      const realH = Math.max(1, real.height);
      const centerMinX = inner.xMin + ((inner.xMax - inner.xMin) - realW) / 2;
      const centerMinY = inner.yMin + ((inner.yMax - inner.yMin) - realH) / 2;
      return movePieceRealMinTo(copy, centerMinX, centerMinY);
    }

    // Calcular destino mínimo para manter dentro dos limites pelas bordas REAIS
    let targetMinX = real.minX;
    let targetMinY = real.minY;

    if (real.minX < inner.xMin) targetMinX = inner.xMin;
    if (real.maxX > inner.xMax) targetMinX = Math.min(targetMinX, inner.xMax - real.width);

    if (real.minY < inner.yMin) targetMinY = inner.yMin;
    if (real.maxY > inner.yMax) targetMinY = Math.min(targetMinY, inner.yMax - real.height);

    // Aplicar apenas se necessário
    if (targetMinX !== real.minX || targetMinY !== real.minY) {
      return movePieceRealMinTo(copy, targetMinX, targetMinY);
    }
    return copy;
  }, [getInnerBounds, getRealDimensions, movePieceRealMinTo]);

  // Detecção de colisão respeitando espaçamento (usando bbox rotacionado)
  const detectCollisionWithSpacing = useCallback((pieceA, pieceB, spacing) => {
    const bboxA = rotatedBBoxFromPiece(pieceA, getPiecePoints, getRotationCenter, rotatePoints, getTransformedPoints);
    const bboxB = rotatedBBoxFromPiece(pieceB, getPiecePoints, getRotationCenter, rotatePoints, getTransformedPoints);
    const s = Number(spacing) || 0;
    const expandedA = { x: bboxA.x - s, y: bboxA.y - s, width: bboxA.width + 2 * s, height: bboxA.height + 2 * s };
    const expandedB = { x: bboxB.x - s, y: bboxB.y - s, width: bboxB.width + 2 * s, height: bboxB.height + 2 * s };
    return !(
      expandedA.x + expandedA.width < expandedB.x ||
      expandedB.x + expandedB.width < expandedA.x ||
      expandedA.y + expandedA.height < expandedB.y ||
      expandedB.y + expandedB.height < expandedA.y
    );
  }, [getPiecePoints, getRotationCenter, getTransformedPoints, rotatePoints, rotatedBBoxFromPiece]);


  // Parâmetro para suavização de colisão - agora 0 para respeitar exatamente o espaçamento
  const colisaoSuavePx = 0; // Bloqueia apenas quando invadir o espaçamento definido

  // Detecção de colisão usando PONTOS EXATOS com atalho rápido por bbox e amostragem para shapes densos
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const detectCollisionWithExactPoints = useCallback((pieceA, pieceB, spacing) => {
    const spacingPx = Number(spacing) || 0;

    // 0) Atalho rápido: bbox de A expandido pelo espaçamento vs bbox de B (sem expansão)
    // Isso garante que o espaçamento de A encosta na borda de B, não espaçamento + espaçamento
    const bboxA = rotatedBBoxFromPiece(pieceA, getPiecePoints, getRotationCenter, rotatePoints, getTransformedPoints);
    const bboxB = rotatedBBoxFromPiece(pieceB, getPiecePoints, getRotationCenter, rotatePoints, getTransformedPoints);
    const expandedA = { x: bboxA.x - spacingPx, y: bboxA.y - spacingPx, width: bboxA.width + 2 * spacingPx, height: bboxA.height + 2 * spacingPx };
    // bboxB permanece sem expansão - é a borda real da peça
    const boxesIntersect = !(
      expandedA.x + expandedA.width < bboxB.x ||
      bboxB.x + bboxB.width < expandedA.x ||
      expandedA.y + expandedA.height < bboxB.y ||
      bboxB.y + bboxB.height < expandedA.y
    );
    if (!boxesIntersect) return false;

    // 1) Obter pontos TRANSFORMADOS (com rotação e translação)
    let pointsA = getTransformedPoints(pieceA);
    let pointsB = getTransformedPoints(pieceB);

    // Fallback para bbox se não houver pontos
    if (!pointsA.length || !pointsB.length) {
      return detectCollisionWithSpacing(pieceA, pieceB, spacingPx);
    }

    // Amostragem suave para detecção de colisão (resolução máxima)
    // Reduzida de 1200 para 400 pontos para melhor performance em peças complexas
    const downSample = (arr, target = 400) => {
      const len = arr.length;
      if (len <= target) return arr;
      const step = len / target;
      const out = [];
      for (let i = 0; i < len; i += step) out.push(arr[Math.floor(i)]);
      // manter fechamento para polígonos
      if (out.length && (out[0][0] !== out[out.length - 1][0] || out[0][1] !== out[out.length - 1][1])) {
        out.push([...out[0]]);
      }
      return out;
    };

    if (pointsA.length > 1000) pointsA = downSample(pointsA, 400);
    if (pointsB.length > 1000) pointsB = downSample(pointsB, 400);

    // 2) Teste ponto->segmento: distância da borda de A até borda de B
    // O espaçamento é considerado apenas UMA VEZ (não duplicado)
    // Verifica se algum ponto de A está muito próximo de B (invadindo o spacing)
    let menorDist = Infinity;
    const threshold = spacingPx - colisaoSuavePx;
    
    // Testar apenas distância de pontos de A até segmentos de B
    // Isso garante que o espaçamento de A encosta na borda real de B
    for (const [ax, ay] of pointsA) {
      for (let i = 0; i < pointsB.length; i++) {
        const [bx1, by1] = pointsB[i];
        const [bx2, by2] = pointsB[(i + 1) % pointsB.length];
        const distance = pointToLineDistance(ax, ay, bx1, by1, bx2, by2);
        if (distance < menorDist) menorDist = distance;
        if (distance <= threshold) return true;
      }
      // Early exit se já sabemos que está longe
      if (menorDist > spacingPx * 3) break;
    }
    
    // E também testar pontos de B até segmentos de A (simétrico)
    for (const [bx, by] of pointsB) {
      for (let i = 0; i < pointsA.length; i++) {
        const [ax1, ay1] = pointsA[i];
        const [ax2, ay2] = pointsA[(i + 1) % pointsA.length];
        const distance = pointToLineDistance(bx, by, ax1, ay1, ax2, ay2);
        if (distance < menorDist) menorDist = distance;
        if (distance <= threshold) return true;
      }
    }
    // Só bloqueia se a sobreposição for maior que o permitido
    return false;
  }, [getPiecePoints, getRotationCenter, getTransformedPoints, rotatePoints, detectCollisionWithSpacing, rotatedBBoxFromPiece]);


  // Desliza horizontalmente até encostar (respeitando espaçamento) nas peças já posicionadas
  // eslint-disable-next-line no-use-before-define
  const placeHorizontallyWithMinimalGap = useCallback((basePiece, startY, rowPieces, inner) => {
    const spacingPx = getSpacingPx();
    const others = rowPieces || [];
    const currentMaxX = others.length ? Math.max(...others.map(op => getRealDimensions(op).maxX)) : inner.xMin;
    const safeStartX = (others.length ? currentMaxX + spacingPx + 5 : inner.xMin);

    let candidate = movePieceRealMinTo({ ...basePiece }, safeStartX, startY);
    candidate = clampPieceToMaterial(candidate);

    const collidesWithAny = (cand) => others.some(op => detectCollisionWithExactPoints(cand, op, spacingPx));
    let attempts = 0;
    const maxAttempts = 20000; // limite alto para layouts grandes

    // 1) Deslizar para ESQUERDA até colidir
    while (!collidesWithAny(candidate) && attempts < maxAttempts) {
      const moved = deltaMovePiece(candidate, -1, 0);
      const real = getRealDimensions(moved);
      if (real.minX < inner.xMin) { break; }
      candidate = moved;
      attempts++;
    }
    // 2) Se colidiu, dar um passo para DIREITA até não colidir
    while (collidesWithAny(candidate) && attempts < maxAttempts) {
      candidate = deltaMovePiece(candidate, 1, 0);
      attempts++;
    }

    candidate = clampPieceToMaterial(candidate);
    return candidate;
  }, [getSpacingPx, movePieceRealMinTo, deltaMovePiece, getRealDimensions, clampPieceToMaterial, detectCollisionWithExactPoints]);

  // Desliza verticalmente até encostar (respeitando espaçamento) nas peças já posicionadas
  // eslint-disable-next-line no-use-before-define
  const placeVerticallyWithMinimalGap = useCallback((basePiece, targetMinX, placedPieces, inner) => {
    const spacingPx = getSpacingPx();
    const others = placedPieces || [];
    const currentMaxY = others.length ? Math.max(...others.map(op => getRealDimensions(op).maxY)) : inner.yMin;
    const safeStartY = (others.length ? currentMaxY + spacingPx + 5 : inner.yMin);

    let candidate = movePieceRealMinTo({ ...basePiece }, targetMinX, safeStartY);
    candidate = clampPieceToMaterial(candidate);

    const collidesWithAny = (cand) => others.some(op => detectCollisionWithExactPoints(cand, op, spacingPx));
    let attempts = 0;
    const maxAttempts = 20000;

    // 1) Deslizar para CIMA até colidir
    while (!collidesWithAny(candidate) && attempts < maxAttempts) {
      const moved = deltaMovePiece(candidate, 0, -1);
      const real = getRealDimensions(moved);
      if (real.minY < inner.yMin) { break; }
      candidate = moved;
      attempts++;
    }
    // 2) Se colidiu, dar um passo para BAIXO até não colidir
    while (collidesWithAny(candidate) && attempts < maxAttempts) {
      candidate = deltaMovePiece(candidate, 0, 1);
      attempts++;
    }

    candidate = clampPieceToMaterial(candidate);
    return candidate;
  }, [getSpacingPx, movePieceRealMinTo, deltaMovePiece, getRealDimensions, clampPieceToMaterial, detectCollisionWithExactPoints]);

  // Resolver colisões mantendo ancoragem em margem (move apenas no eixo perpendicular à margem)
  // eslint-disable-next-line no-use-before-define
  const resolveCollisionsWithAnchor = useCallback((candidateInit, others, anchorFlags, inner) => {
    const spacingPx = getSpacingPx();
    let candidate = { ...candidateInit };
    const maxAttempts = 10000;
    let attempts = 0;

    const isAnchoredH = anchorFlags.left || anchorFlags.right;   // âncora vertical nas laterais => mover no eixo Y
    const isAnchoredV = anchorFlags.top || anchorFlags.bottom;   // âncora horizontal no topo/base => mover no eixo X

    const anyCollision = (cand) => others.some((op) => detectCollisionWithExactPoints(cand, op, spacingPx));
    const center = (p) => {
      const r = rotatedBBoxFromPiece(p, getPiecePoints, getRotationCenter, rotatePoints, getTransformedPoints);
      return { cx: r.x + r.width / 2, cy: r.y + r.height / 2 };
    };

    // Iterar até não haver colisões
    while (anyCollision(candidate) && attempts < maxAttempts) {
      // Encontrar um dos que colidem para decidir direção
      // eslint-disable-next-line no-loop-func
      const coll = others.find(op => detectCollisionWithExactPoints(candidate, op, spacingPx));
      if (!coll) break;
      const { cx: cCx, cy: cCy } = center(candidate);
      const { cx: oCx, cy: oCy } = center(coll);

      if (isAnchoredH && !isAnchoredV) {
        // mover apenas em Y
        const dirY = (cCy >= oCy) ? 1 : -1; // para longe do outro
        const moved = deltaMovePiece(candidate, 0, dirY);
        // re-ancorar em X
        if (anchorFlags.left) {
          candidate = movePieceRealMinTo(moved, inner.xMin, getRealDimensions(moved).minY);
        } else if (anchorFlags.right) {
          candidate = movePieceRealMaxTo(moved, inner.xMax, getRealDimensions(moved).maxY);
        } else {
          candidate = moved;
        }
      } else if (isAnchoredV && !isAnchoredH) {
        // mover apenas em X
        const dirX = (cCx >= oCx) ? 1 : -1;
        const moved = deltaMovePiece(candidate, dirX, 0);
        // re-ancorar em Y
        if (anchorFlags.top) {
          candidate = movePieceRealMinTo(moved, getRealDimensions(moved).minX, inner.yMin);
        } else if (anchorFlags.bottom) {
          candidate = movePieceRealMaxTo(moved, getRealDimensions(moved).maxX, inner.yMax);
        } else {
          candidate = moved;
        }
      } else {
        // Se estava ancorado em ambos (canto) ou em nenhum, usar pequeno ajuste diagonal (fallback)
        const dirX = (cCx >= oCx) ? 1 : -1;
        const dirY = (cCy >= oCy) ? 1 : -1;
        candidate = deltaMovePiece(candidate, dirX, dirY);
      }
      // Garantir dentro da área útil
      candidate = clampPieceToMaterial(candidate);
      attempts++;
    }
    return candidate;
  }, [getPiecePoints, getSpacingPx, getRotationCenter, getTransformedPoints, rotatePoints, detectCollisionWithExactPoints, deltaMovePiece, movePieceRealMinTo, movePieceRealMaxTo, getRealDimensions, clampPieceToMaterial, rotatedBBoxFromPiece]);

  
  // Snap corrigido para margens e outras peças usando contorno REAL (não apenas bbox)
  // eslint-disable-next-line no-use-before-define
  const applySnap = useCallback((candidate, index, others) => {
    const inner = getInnerBounds();
    const spacingPx = getSpacingPx();
    const snapThreshold = SNAP_PX;

    let snapped = { ...candidate };
    let changed = false;

    // Dimensões reais atuais
    let real = getRealDimensions(snapped);

    // PRÉ-CHEQUE: verificar se está perto de alguma margem antes de calcular snap
    const nearMarginX = Math.abs(real.minX - inner.xMin) <= snapThreshold || Math.abs(real.maxX - inner.xMax) <= snapThreshold;
    const nearMarginY = Math.abs(real.minY - inner.yMin) <= snapThreshold || Math.abs(real.maxY - inner.yMax) <= snapThreshold;

    // Snap nas margens pela borda REAL (só se estiver perto)
    if (nearMarginX) {
      if (Math.abs(real.minX - inner.xMin) <= snapThreshold) {
        snapped = movePieceRealMinTo(snapped, inner.xMin, real.minY);
        real = getRealDimensions(snapped);
        changed = true;
      } else if (Math.abs(real.maxX - inner.xMax) <= snapThreshold) {
        snapped = movePieceRealMaxTo(snapped, inner.xMax, real.maxY);
        real = getRealDimensions(snapped);
        changed = true;
      }
    }

    if (nearMarginY) {
      if (Math.abs(real.minY - inner.yMin) <= snapThreshold) {
        snapped = movePieceRealMinTo(snapped, real.minX, inner.yMin);
        real = getRealDimensions(snapped);
        changed = true;
      } else if (Math.abs(real.maxY - inner.yMax) <= snapThreshold) {
        snapped = movePieceRealMaxTo(snapped, real.maxX, inner.yMax);
        real = getRealDimensions(snapped);
        changed = true;
      }
    }

    // Snap em relação a outras peças (borda REAL + espaçamento)
    // PRÉ-FILTRO: só processar peças que estão próximas o suficiente
    const nearbyPieces = (others || []).filter((other, j) => {
      if (j === index) return false;
      const oReal = getRealDimensions(other);
      // Checar se há sobreposição ou proximidade nas dimensões
      const xOverlap = !(real.maxX + snapThreshold < oReal.minX || real.minX - snapThreshold > oReal.maxX);
      const yOverlap = !(real.maxY + snapThreshold < oReal.minY || real.minY - snapThreshold > oReal.maxY);
      return xOverlap || yOverlap;
    });

    nearbyPieces.forEach((other, j) => {
      const oReal = getRealDimensions(other);

      // Direita do candidato tocando esquerda da outra com espaçamento
      if (Math.abs(real.maxX - oReal.minX) <= snapThreshold) {
        const targetMaxX = oReal.minX - spacingPx;
        snapped = movePieceRealMaxTo(snapped, targetMaxX, real.maxY);
        real = getRealDimensions(snapped);
        changed = true;
      } else if (Math.abs(real.minX - oReal.maxX) <= snapThreshold) {
        // Esquerda do candidato tocando direita da outra com espaçamento
        const targetMinX = oReal.maxX + spacingPx;
        snapped = movePieceRealMinTo(snapped, targetMinX, real.minY);
        real = getRealDimensions(snapped);
        changed = true;
      }

      // Base do candidato tocando topo da outra com espaçamento
      if (Math.abs(real.maxY - oReal.minY) <= snapThreshold) {
        const targetMaxY = oReal.minY - spacingPx;
        snapped = movePieceRealMaxTo(snapped, real.maxX, targetMaxY);
        real = getRealDimensions(snapped);
        changed = true;
      } else if (Math.abs(real.minY - oReal.maxY) <= snapThreshold) {
        // Topo do candidato tocando base da outra com espaçamento
        const targetMinY = oReal.maxY + spacingPx;
        snapped = movePieceRealMinTo(snapped, real.minX, targetMinY);
        real = getRealDimensions(snapped);
        changed = true;
      }
    });

    if (changed) {
      // Garantir permanência dentro da área útil após o snap
      return clampPieceToMaterial(snapped);
    }
    return candidate;
  }, [getInnerBounds, getSpacingPx, getRealDimensions, movePieceRealMinTo, movePieceRealMaxTo, clampPieceToMaterial]);

  // Criar uma fileira HORIZONTAL manual a partir da peça selecionada
  // eslint-disable-next-line no-unused-vars
  const criarFileiraHorizontal = useCallback(() => {
    if (!pieces.length || selected === null) return;
    const piece = pieces[selected];
    const real = getRealDimensions(piece);
    const larguraReal = real.width;
    const inner = getInnerBounds();
    const spacingPx = getSpacingPx();

    // DEBUG: validações de espaçamento e dimensões reais
    try {
      const bbox = rotatedBBoxFromPiece(piece, getPiecePoints, getRotationCenter, rotatePoints, getTransformedPoints);
      console.log('DEBUG - Criar Fileira Horizontal:', {
        spacingPx,
        larguraBBox: bbox.width,
        larguraReal,
        diferenca: bbox.width - larguraReal
      });
    } catch {}

  const startX = inner.xMin;
  // Preservar Y original do contorno real da peça selecionada
  const startY = getRealDimensions(piece).minY;

    // Mover peça selecionada para a posição inicial (topo-esquerda útil)
  // Encostar contorno REAL do primeiro elemento à margem ESQUERDA, preservando Y original
  let primeira = movePieceRealMinTo(piece, startX, startY);
    primeira = clampPieceToMaterial(primeira);

    const novasPecas = [...pieces];
    // Substitui selecionada em nova lista (será removida ao montar conjunto final)
    novasPecas[selected] = primeira;

    const fileira = [primeira];
    // Preencher a linha deslizando cada nova peça até encostar (com o espaçamento)
    while (true) {
      // posição segura inicial baseada no fim atual da linha
      const next = placeHorizontallyWithMinimalGap(piece, startY, fileira, inner);
      const realNext = getRealDimensions(next);
      if (realNext.maxX > inner.xMax - 0.5) break;
      // Evitar duplicar a mesma posição (quando não há espaço para mover)
      const last = fileira[fileira.length - 1];
      const lastReal = getRealDimensions(last);
      if (Math.abs(realNext.minX - lastReal.minX) < 1 && Math.abs(realNext.minY - lastReal.minY) < 1) break;
      fileira.push(next);
      // proteção contra loop infinito
      if (fileira.length > 5000) break;
    }

    setFileiraVertical(fileira); // Mantemos o nome da state por compatibilidade
    // Remove a original selecionada e adiciona toda fileira
    setPieces([...novasPecas.filter((_, i) => i !== selected), ...fileira]);
  }, [getPiecePoints, getRotationCenter, getTransformedPoints, rotatePoints, pieces, selected, getRealDimensions, getInnerBounds, getSpacingPx, placeHorizontallyWithMinimalGap, movePieceRealMinTo, clampPieceToMaterial, rotatedBBoxFromPiece]);

  // Criar uma fileira HORIZONTAL a partir de múltiplas peças selecionadas (repetindo peças individualmente em sequência)
  // eslint-disable-next-line no-unused-vars
  const criarFileiraHorizontalMultipla = useCallback(() => {
    if (selectedPieces.size < 2) {
      alert('Selecione pelo menos 2 peças com Ctrl+Clique');
      return;
    }

    const selectedIndices = Array.from(selectedPieces);
    const selectedPiecesData = selectedIndices.map(index => pieces[index]).filter(Boolean);
    if (selectedPiecesData.length < 2) return;

    const inner = getInnerBounds();
    // Ordena pela posição X para manter sequência
    const ordered = [...selectedPiecesData].sort((a, b) => (a.x || 0) - (b.x || 0));

    const rowPieces = [];
    let idx = 0;
    // Tenta colocar peças ciclicamente até não caber mais, sempre encostando com o espaçamento
    while (ordered.length > 0) {
  const piece = ordered[idx % ordered.length];
  // Manter a posição vertical ORIGINAL de cada peça
  const startYForPiece = getRealDimensions(piece).minY;
  // Posiciona com deslize mínimo em relação às já colocadas, sem alterar o Y original
  const nova = placeHorizontallyWithMinimalGap(piece, startYForPiece, rowPieces, inner);
      const real = getRealDimensions(nova);
      if (real.maxX > inner.xMax - 0.5) break;
      rowPieces.push(nova);
  // currentX removed
      idx++;
      if (idx > 10000) break;
    }

    if (!rowPieces.length) {
      alert('Nenhuma peça da sequência cabe na largura do material.');
      return;
    }

    // Atualizar peças: removes originais selecionadas e adiciona a fileira sequencial
    setPieces(prev => [
      ...prev.filter((_, index) => !selectedPieces.has(index)),
      ...rowPieces
    ]);
    setFileiraVertical(rowPieces);

    setSelected(null);
    setSelectedPieces(new Set());
    setDragging(null);
  }, [selectedPieces, pieces, getInnerBounds, getRealDimensions, placeHorizontallyWithMinimalGap]);

  // Preencher TODO o material com sequência individual (substitui layout)
  // eslint-disable-next-line no-unused-vars, react-hooks/exhaustive-deps
  const preencherMaterialCompletoSequencial = useCallback(() => {
    if (selectedPieces.size < 1) {
      alert('Selecione pelo menos 1 peça com Ctrl+Clique');
      return;
    }

    const selectedIndices = Array.from(selectedPieces);
    const selectedPiecesData = selectedIndices.map(index => pieces[index]).filter(Boolean);
    if (selectedPiecesData.length < 1) return;

    const inner = getInnerBounds();
    const spacingPx = getSpacingPx();

    const ordered = [...selectedPiecesData].sort((a, b) => (a.x || 0) - (b.x || 0));
    const dims = ordered.map(p => {
      const r = getRealDimensions(p);
      return { width: r.width, height: r.height, piece: p };
    });
    const alturaMaxima = Math.max(...dims.map(d => d.height));

    const novas = [];
    let y = inner.yMin;
    while (y + alturaMaxima <= inner.yMax) {
      let x = inner.xMin;
      let idx = 0;
      while (x < inner.xMax) {
        const d = dims[idx % dims.length];
        if (x + d.width > inner.xMax) break;
        let np = movePieceTo({ ...d.piece }, x, y);
        np = clampPieceToMaterial(np);
        novas.push(np);
        x += d.width + spacingPx;
        idx++;
        if (dims.length === 0) break;
      }
      y += alturaMaxima + spacingPx;
      // Proteção caso nenhuma peça caiba na linha
      if (novas.length === 0) break;
    }

    if (!novas.length) {
      alert('Nenhuma peça cabe no material com a sequência selecionada.');
      return;
    }

    setPieces(novas);
    setSelected(null);
    setSelectedPieces(new Set());
    setDragging(null);
    try { console.log(`Preenchido sequencialmente com ${novas.length} peças`); } catch {}
  }, [selectedPieces, pieces, getInnerBounds, getSpacingPx, getRealDimensions, movePieceTo, clampPieceToMaterial]);

  // Preencher adicionando ao layout existente com sequência individual
  // eslint-disable-next-line no-unused-vars
  const preencherMaterialAditivoSequencial = useCallback(() => {
    if (selectedPieces.size < 1) {
      alert('Selecione pelo menos 1 peça com Ctrl+Clique');
      return;
    }

    const selectedIndices = Array.from(selectedPieces);
    const selectedPiecesData = selectedIndices.map(index => pieces[index]).filter(Boolean);
    if (selectedPiecesData.length < 1) return;

    const inner = getInnerBounds();
    const spacingPx = getSpacingPx();

    const ordered = [...selectedPiecesData].sort((a, b) => (a.x || 0) - (b.x || 0));
    const dims = ordered.map(p => {
      const r = getRealDimensions(p);
      return { width: r.width, height: r.height, piece: p };
    });
    const alturaMaxima = Math.max(...dims.map(d => d.height));

    const novas = [...pieces];
    let y = inner.yMin;
    while (y + alturaMaxima <= inner.yMax) {
      let x = inner.xMin;
      let idx = 0;
      while (x < inner.xMax) {
        const d = dims[idx % dims.length];
        if (x + d.width > inner.xMax) break;
        let np = movePieceTo({ ...d.piece }, x, y);
        np = clampPieceToMaterial(np);
        novas.push(np);
        x += d.width + spacingPx;
        idx++;
        if (dims.length === 0) break;
      }
      y += alturaMaxima + spacingPx;
      if (idx === 0) break;
    }

    setPieces(novas);
    try { console.log(`Adicionado preenchimento sequencial com ${novas.length - pieces.length} peças`); } catch {}
  }, [selectedPieces, pieces, getInnerBounds, getSpacingPx, getRealDimensions, movePieceTo, clampPieceToMaterial]);


  // Duplicar automaticamente a fileira horizontal para preencher VERTICALMENTE
  // eslint-disable-next-line no-unused-vars
  const preencherMaterialAutomatico = useCallback(() => {
    if (!fileiraVertical.length) {
      alert('Primeiro crie uma fileira horizontal manual!');
      return;
    }
    const inner = getInnerBounds();

    // Copiar a primeira fileira como base
    const todas = [...fileiraVertical];

    // Repetir fileiras para baixo com encaixe vertical mínimo
    while (true) {
      let anyPlaced = false;
      for (const orig of fileiraVertical) {
        const targetMinX = getRealDimensions(orig).minX; // manter coluna
        const nova = placeVerticallyWithMinimalGap(orig, targetMinX, todas, inner);
        const real = getRealDimensions(nova);
        // Se ultrapassar o limite inferior, encerra
        if (real.maxY > inner.yMax - 0.5) {
          anyPlaced = false;
          break;
        }
        todas.push(nova);
        anyPlaced = true;
      }
      if (!anyPlaced) break;
      if (todas.length > 20000) break;
    }

    setPieces(todas);
  }, [fileiraVertical, getInnerBounds, getRealDimensions, placeVerticallyWithMinimalGap]);

  // (removido) criarFileiraHorizontalInteligente não utilizado no momento

  // Nova função: preencher TODO o material com o grupo selecionado (substitui tudo)
  // eslint-disable-next-line no-unused-vars, react-hooks/exhaustive-deps
  const preencherMaterialCompleto = useCallback(() => {
    if (selectedPieces.size < 1) {
      alert('Selecione pelo menos 1 peça com Ctrl+Clique');
      return;
    }

    const selectedIndices = Array.from(selectedPieces);
    let selectedPiecesData = selectedIndices.map(index => pieces[index]).filter(Boolean);
    if (selectedPiecesData.length < 1) return;

    const inner = getInnerBounds();
    const spacingPx = getSpacingPx();

    // Caso especial: se o usuário acabou de criar uma fileira (fileiraVertical) e quer preencher tudo,
    // use essa fileira como padrão para azulejar o material verticalmente.
    if (fileiraVertical && fileiraVertical.length >= 1 && selectedPiecesData.length >= 1) {
      const baseRow = fileiraVertical;
      const innerBounds = inner;
      const spacingPx = getSpacingPx();

      // 1) Alinhar a fileira base ao canto superior esquerdo da área útil
      const offsets = baseRow.map(p => {
        const r = getRealDimensions(p);
        return { piece: p, offX: r.minX, offY: r.minY };
      });
      const rowRealMinX = Math.min(...offsets.map(o => o.offX));
      const rowRealMinY = Math.min(...offsets.map(o => o.offY));
      const dxAlign = innerBounds.xMin - rowRealMinX;
      const dyAlign = innerBounds.yMin - rowRealMinY;
      const firstRowAligned = offsets.map(({ piece }) => clampPieceToMaterial(deltaMovePiece({ ...piece }, dxAlign, dyAlign)));

      // 2) Empilhar por coluna com deslocamento vertical mínimo (apenas espaçamento)
      const todas = [...firstRowAligned];
      const columns = firstRowAligned.map(p => [p]);

      // Limite de segurança de iterações baseado na altura disponível e menor altura de coluna
      const colHeights = firstRowAligned.map(p => getRealDimensions(p).height || 1);
      const minColH = Math.max(1, Math.min(...colHeights));
      const maxRowsEst = Math.ceil((innerBounds.yMax - innerBounds.yMin) / (minColH + spacingPx)) + 2;

      for (let row = 1; row <= maxRowsEst; row++) {
        let placedThisRow = false;
        for (let i = 0; i < columns.length; i++) {
          const colStack = columns[i];
          const baseColPiece = firstRowAligned[i];
          const last = colStack[colStack.length - 1];
          const lastReal = getRealDimensions(last);
          const baseColReal = getRealDimensions(baseColPiece);
          const targetMinX = baseColReal.minX;
          const targetMinY = lastReal.maxY + spacingPx;

          // Criar próxima peça exatamente abaixo da anterior na mesma coluna
          let next = movePieceRealMinTo({ ...baseRow[i] }, targetMinX, targetMinY);
          next = clampPieceToMaterial(next);
          const nextReal = getRealDimensions(next);
          if (nextReal.maxY > innerBounds.yMax - 0.5) {
            continue; // esta coluna não comporta mais peças
          }
          colStack.push(next);
          todas.push(next);
          placedThisRow = true;
          if (todas.length > 30000) break;
        }
        if (!placedThisRow || todas.length > 30000) break;
      }

      setPieces(todas);
      setSelected(null);
      setSelectedPieces(new Set());
      setDragging(null);
      return;
    }

    // Bounding box do grupo
    let groupMinX = Infinity, groupMinY = Infinity, groupMaxX = -Infinity, groupMaxY = -Infinity;
    selectedPiecesData.forEach(piece => {
      // Use pontos reais para delimitar o grupo
      const real = getRealDimensions(piece);
      groupMinX = Math.min(groupMinX, real.minX);
      groupMinY = Math.min(groupMinY, real.minY);
      groupMaxX = Math.max(groupMaxX, real.maxX);
      groupMaxY = Math.max(groupMaxY, real.maxY);
    });
    const groupWidth = groupMaxX - groupMinX;
    const groupHeight = groupMaxY - groupMinY;

    // Offsets relativos
    const relativeOffsets = selectedPiecesData.map(piece => {
      const real = getRealDimensions(piece);
      return { piece, offsetX: real.minX - groupMinX, offsetY: real.minY - groupMinY };
    });

    const novasPecas = [];
    const innerW = inner.xMax - inner.xMin;
    const innerH = inner.yMax - inner.yMin;
    // Permitir pelo menos 1 repetição quando o grupo cabe no eixo
    const repeticoesX = (groupWidth <= innerW + 1e-3)
      ? Math.max(1, Math.floor((innerW + spacingPx) / (groupWidth + spacingPx)))
      : 0;
    const repeticoesY = (groupHeight <= innerH + 1e-3)
      ? Math.max(1, Math.floor((innerH + spacingPx) / (groupHeight + spacingPx)))
      : 0;
    if (repeticoesX < 1 || repeticoesY < 1) {
      alert('O grupo de peças é muito grande para caber no material!');
      return;
    }

    for (let linha = 0; linha < repeticoesY; linha++) {
      for (let coluna = 0; coluna < repeticoesX; coluna++) {
        const baseX = inner.xMin + coluna * (groupWidth + spacingPx);
        const baseY = inner.yMin + linha * (groupHeight + spacingPx);
        relativeOffsets.forEach(({ piece, offsetX, offsetY }) => {
          // Alinhar cada peça pelo seu contorno REAL (minX/minY) ao deslocamento do grupo
          const targetMinX = baseX + offsetX;
          const targetMinY = baseY + offsetY;
          let novaPiece = movePieceRealMinTo({ ...piece }, targetMinX, targetMinY);
          novaPiece = clampPieceToMaterial(novaPiece);
          novasPecas.push(novaPiece);
        });
      }
    }

    setPieces(novasPecas);
    setSelected(null);
    setSelectedPieces(new Set());
    setDragging(null);

    try { console.log(`Preenchido com ${repeticoesX}x${repeticoesY} grupos (${novasPecas.length} peças no total)`); } catch {}
  }, [selectedPieces, pieces, getInnerBounds, getSpacingPx, getRealDimensions, movePieceRealMinTo, clampPieceToMaterial, fileiraVertical, deltaMovePiece]);

  // Nova função: preencher material adicionando ao layout existente
  // eslint-disable-next-line no-unused-vars
  const preencherMaterialAditivo = useCallback(() => {
    if (selectedPieces.size < 1) {
      alert('Selecione pelo menos 1 peça com Ctrl+Clique');
      return;
    }

    const selectedIndices = Array.from(selectedPieces);
    const selectedPiecesData = selectedIndices.map(index => pieces[index]).filter(Boolean);
    if (selectedPiecesData.length < 1) return;

    const inner = getInnerBounds();
    const spacingPx = getSpacingPx();

    // Bounding box do grupo
    let groupMinX = Infinity, groupMinY = Infinity, groupMaxX = -Infinity, groupMaxY = -Infinity;
    selectedPiecesData.forEach(piece => {
      const real = getRealDimensions(piece);
      groupMinX = Math.min(groupMinX, real.minX);
      groupMinY = Math.min(groupMinY, real.minY);
      groupMaxX = Math.max(groupMaxX, real.maxX);
      groupMaxY = Math.max(groupMaxY, real.maxY);
    });
    const groupWidth = groupMaxX - groupMinX;
    const groupHeight = groupMaxY - groupMinY;

    const relativeOffsets = selectedPiecesData.map(piece => {
      const real = getRealDimensions(piece);
      return { piece, offsetX: real.minX - groupMinX, offsetY: real.minY - groupMinY };
    });

    const novasPecas = [...pieces];
    const innerW2 = inner.xMax - inner.xMin;
    const innerH2 = inner.yMax - inner.yMin;
    const repeticoesX = (groupWidth <= innerW2 + 1e-3)
      ? Math.max(1, Math.floor((innerW2 + spacingPx) / (groupWidth + spacingPx)))
      : 0;
    const repeticoesY = (groupHeight <= innerH2 + 1e-3)
      ? Math.max(1, Math.floor((innerH2 + spacingPx) / (groupHeight + spacingPx)))
      : 0;
    if (repeticoesX < 1 || repeticoesY < 1) {
      alert('O grupo de peças é muito grande para caber no material!');
      return;
    }

    for (let linha = 0; linha < repeticoesY; linha++) {
      for (let coluna = 0; coluna < repeticoesX; coluna++) {
        const baseX = inner.xMin + coluna * (groupWidth + spacingPx);
        const baseY = inner.yMin + linha * (groupHeight + spacingPx);
        relativeOffsets.forEach(({ piece, offsetX, offsetY }) => {
          // Alinhar cada peça pelo seu contorno REAL (minX/minY) ao deslocamento do grupo
          const targetMinX = baseX + offsetX;
          const targetMinY = baseY + offsetY;
          let novaPiece = movePieceRealMinTo({ ...piece }, targetMinX, targetMinY);
          novaPiece = clampPieceToMaterial(novaPiece);
          novasPecas.push(novaPiece);
        });
      }
    }

    setPieces(novasPecas);

    try { console.log(`Adicionado ${repeticoesX * repeticoesY * selectedPiecesData.length} peças ao layout existente`); } catch {}
  }, [selectedPieces, pieces, getInnerBounds, getSpacingPx, getRealDimensions, movePieceRealMinTo, clampPieceToMaterial]);

  // ===== Colisão exata usando os MESMOS pontos da renderização =====
  // Função auxiliar: distância de ponto a segmento de reta
  const pointToLineDistance = (px, py, x1, y1, x2, y2) => {
    const A = px - x1;
    const B = py - y1;
    const C = x2 - x1;
    const D = y2 - y1;

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    let param = -1;

    if (lenSq !== 0) {
      param = dot / lenSq;
    }

    let xx, yy;

    if (param < 0) {
      xx = x1; yy = y1;
    } else if (param > 1) {
      xx = x2; yy = y2;
    } else {
      xx = x1 + param * C;
      yy = y1 + param * D;
    }

    const dx = px - xx;
    const dy = py - yy;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Direção para afastar uma peça da outra
  const findSeparationDirection = useCallback((pieceA, pieceB) => {
    const bboxA = rotatedBBoxFromPiece(pieceA, getPiecePoints, getRotationCenter, rotatePoints, getTransformedPoints);
    const bboxB = rotatedBBoxFromPiece(pieceB, getPiecePoints, getRotationCenter, rotatePoints, getTransformedPoints);

    const centerAX = bboxA.x + bboxA.width / 2;
    const centerAY = bboxA.y + bboxA.height / 2;
    const centerBX = bboxB.x + bboxB.width / 2;
    const centerBY = bboxB.y + bboxB.height / 2;

    const dx = centerBX - centerAX;
    const dy = centerBY - centerAY;
    const length = Math.sqrt(dx * dx + dy * dy);
    if (length === 0) return { x: 1, y: 0 };
    return { x: -dx / length, y: -dy / length };
  }, [getPiecePoints, getRotationCenter, getTransformedPoints, rotatePoints, rotatedBBoxFromPiece]);

  // Ajusta posição para manter espaçamento entre peças
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const clampPieceSpacing = useCallback((candidate, index, otherPieces, allowedAxis = null) => {
    const spacingPx = getSpacingPx();
    let adjusted = { ...candidate };
    let hasAdjusted = false;

    (otherPieces || []).forEach((other, otherIndex) => {
      if (otherIndex === index) return;
      if (detectCollisionWithExactPoints(adjusted, other, spacingPx)) {
        const direction = findSeparationDirection(adjusted, other);
        const step = 2; // step maior para suavizar
        let attempts = 0;
        const maxAttempts = Math.max(8, spacingPx * 2); // limite para evitar loop infinito
        while (detectCollisionWithExactPoints(adjusted, other, spacingPx) && attempts < maxAttempts) {
          // If allowedAxis is provided, restrict separation movement to that axis to avoid drifting
          const moveX = (allowedAxis === 'y') ? 0 : (direction.x * step);
          const moveY = (allowedAxis === 'x') ? 0 : (direction.y * step);
          adjusted = deltaMovePiece(adjusted, moveX, moveY);
          adjusted = clampPieceToMaterial(adjusted);
          attempts++;
        }
        hasAdjusted = true;
      }
    });

    return hasAdjusted ? adjusted : candidate;
  }, [getSpacingPx, detectCollisionWithExactPoints, findSeparationDirection, deltaMovePiece, clampPieceToMaterial]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const centerPieces = useCallback((ents) => {
    if (!ents || ents.length === 0) return ents;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    ents.forEach(p => {
      const bb = bboxOf(p);
      if (bb.width == null || bb.height == null) return;
      minX = Math.min(minX, bb.x);
      minY = Math.min(minY, bb.y);
      maxX = Math.max(maxX, bb.x + bb.width);
      maxY = Math.max(maxY, bb.y + bb.height);
    });
    if (minX === Infinity) return ents;
    const bboxW = maxX - minX, bboxH = maxY - minY;
    const matWpx = materialPixelWidth();
    const matHpx = materialPixelHeight();
    const offsetX = (matWpx / 2) - (minX + bboxW / 2);
    const offsetY = (matHpx / 2) - (minY + bboxH / 2);
    return ents.map(p => {
      const copy = { ...p };
      if (copy.points && copy.points.length) {
        copy.points = copy.points.map(pt => Array.isArray(pt) ? [pt[0] + offsetX, pt[1] + offsetY] : { x: pt.x + offsetX, y: pt.y + offsetY });
      }
      if (copy.center && copy.radius != null) {
        copy.center = [copy.center[0] + offsetX, copy.center[1] + offsetY];
        copy.x = copy.center[0] - copy.radius; copy.y = copy.center[1] - copy.radius;
      }
      if (copy.x != null && copy.y != null) {
        copy.x = copy.x + offsetX; copy.y = copy.y + offsetY;
      }
      const newBb = bboxOf(copy);
      copy.x = newBb.x; copy.y = newBb.y; copy.width = newBb.width; copy.height = newBb.height;
      // clamp to material
      return clampPieceToMaterial(copy);
    });
  }, [materialPixelWidth, materialPixelHeight, clampPieceToMaterial]);

  const adjustViewBox = useCallback((ents) => {
    // Accept empty array: still center on material
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    // include material rectangle in union so material stays visible/centered
    const matW = (config.larguraMaterial || DEFAULT_CONFIG.larguraMaterial) * svgScale;
    const matH = (config.comprimentoMaterial || DEFAULT_CONFIG.comprimentoMaterial) * svgScale;
    // start with material bounds
    minX = Math.min(minX, 0);
    minY = Math.min(minY, 0);
    maxX = Math.max(maxX, matW);
    maxY = Math.max(maxY, matH);
    if (ents && ents.length) {
      ents.forEach(p => {
        const bb = bboxOf(p);
        minX = Math.min(minX, bb.x);
        minY = Math.min(minY, bb.y);
        maxX = Math.max(maxX, bb.x + bb.width);
        maxY = Math.max(maxY, bb.y + bb.height);
      });
    }
    if (minX === Infinity) return;
    const margin = 40;
    // Calcula largura e altura do conteúdo + margem
    let vbX = Math.floor(minX - margin);
    let vbY = Math.floor(minY - margin);
    let vbW = Math.ceil(maxX - minX + margin * 2);
    let vbH = Math.ceil(maxY - minY + margin * 2);

    // Força proporção igual à do material
    const matAspect = matW / matH;
    const vbAspect = vbW / vbH;
    if (vbAspect > matAspect) {
      // ViewBox está mais "largo" que o material, aumenta altura
      const newVbH = vbW / matAspect;
      const delta = newVbH - vbH;
      vbY -= Math.floor(delta / 2);
      vbH = Math.ceil(newVbH);
    } else if (vbAspect < matAspect) {
      // ViewBox está mais "alto" que o material, aumenta largura
      const newVbW = vbH * matAspect;
      const delta = newVbW - vbW;
      vbX -= Math.floor(delta / 2);
      vbW = Math.ceil(newVbW);
    }
    setViewBox(`${vbX} ${vbY} ${vbW} ${vbH}`);
  }, [config.comprimentoMaterial, config.larguraMaterial, svgScale]);

  // eslint-disable-next-line no-unused-vars
  const handleFileChange = (e) => { if (e.target.files && e.target.files[0]) setDxfFile(e.target.files[0]); };
  
  // Conversão e cálculo de dimensões em cm do retângulo Repet atual
  const getRepetDimsCm = useCallback(() => {
    if (!repetRect) return { larguraCm: 0, alturaCm: 0 };
    const larguraPx = Math.abs((repetRect.x1 ?? 0) - (repetRect.x0 ?? 0));
    const alturaPx = Math.abs((repetRect.y1 ?? 0) - (repetRect.y0 ?? 0));
    const larguraCm = (larguraPx / svgScale) / 10;
    const alturaCm = (alturaPx / svgScale) / 10;
    return { larguraCm, alturaCm };
  }, [repetRect, svgScale]);

  // Parser de viewBox memoizado
  const parseViewBox = useCallback((vb) => {
    const parts = String(vb).split(/\s+/).map(Number);
    if (parts.length !== 4 || parts.some(isNaN)) return { x: 0, y: 0, w: (config.larguraMaterial || 0) * svgScale, h: (config.comprimentoMaterial || 0) * svgScale };
    return { x: parts[0], y: parts[1], w: parts[2], h: parts[3] };
  }, [config.comprimentoMaterial, config.larguraMaterial, svgScale]);

  // Ancoragem/seleção no modo Repet feita diretamente nos handlers com snapping

  // Handlers de desenho do retângulo Repet
  const onSvgMouseDown = useCallback((e) => {
    const svg = svgRef.current; if (!svg) return;
    // Middle-click starts panning universally
    if (e.button === 1) {
      try { e.preventDefault(); e.stopPropagation(); } catch {}
      const vb = parseViewBox(viewBox);
      panRef.current = { startX: e.clientX, startY: e.clientY, vb };
      setIsPanning(true);
      return;
    }
    // In Repet mode: left button draws rectangle with snapping to pieces
    if (repetMode && e.button === 0) {
      try { e.preventDefault(); e.stopPropagation(); } catch {}
      const pt = svg.createSVGPoint(); pt.x = e.clientX; pt.y = e.clientY;
      const ctm = svg.getScreenCTM();
      const p = ctm ? pt.matrixTransform(ctm.inverse()) : { x: 0, y: 0 };
      // Snap forte: pega o ponto mais próximo dentro do threshold (aumentado para facilitar snap visual)
      const snapThreshold = 40;
      let snapped = { x: p.x, y: p.y, found: false, dist: snapThreshold };
      for (let i = 0; i < pieces.length; i++) {
        const renderPts = getRenderPoints(pieces[i]);
        if (!renderPts || !renderPts.length) continue;
        for (const [px, py] of renderPts) {
          const dist = Math.sqrt((p.x - px) ** 2 + (p.y - py) ** 2);
          if (dist < snapped.dist) {
            snapped = { x: px, y: py, found: true, dist };
          }
        }
      }

      // Garante que o ponto inicial do retângulo é exatamente o highlight (círculo azul), se existir
      // Força o cálculo do snapHighlight no mouseDown para garantir sincronismo
      // Sempre calcula o snap do zero, igual ao highlight
      let best = { x: p.x, y: p.y, found: false, dist: snapThreshold };
      for (let i = 0; i < pieces.length; i++) {
        const renderPts = getRenderPoints(pieces[i]);
        if (!renderPts || !renderPts.length) continue;
        for (const [px, py] of renderPts) {
          const dist = Math.sqrt((p.x - px) ** 2 + (p.y - py) ** 2);
          if (dist < best.dist) {
            best = { x: px, y: py, found: true, dist };
          }
        }
      }
      const snapPoint = best.found ? { x: best.x, y: best.y } : { x: p.x, y: p.y };
      setSnapHighlight(snapPoint); // Atualiza highlight para garantir visual
      setRepetAnchorStart(null);
      setIsDrawingRepet(true);
      setRepetStart({ x: snapPoint.x, y: snapPoint.y });
      setRepetRect({ x0: snapPoint.x, y0: snapPoint.y, x1: snapPoint.x, y1: snapPoint.y });
    } else if (e.button === 0 && !repetMode) {
      // Modo de seleção por arrasto (fora do modo Repet)
      try { e.preventDefault(); e.stopPropagation(); } catch {}
      const pt = svg.createSVGPoint(); pt.x = e.clientX; pt.y = e.clientY;
      const ctm = svg.getScreenCTM();
      const p = ctm ? pt.matrixTransform(ctm.inverse()) : { x: 0, y: 0 };
      
      setIsSelecting(true);
      setSelectionStart({ x: p.x, y: p.y });
      setSelectionBox({ x0: p.x, y0: p.y, x1: p.x, y1: p.y });
    }
  }, [repetMode, viewBox, parseViewBox, pieces, getRenderPoints]);

  const onSvgMouseMove = useCallback((e) => {
    if (repetMode && isDrawingRepet) {
      const svg = svgRef.current; if (!svg) return;
      try { e.preventDefault(); e.stopPropagation(); } catch {}
      const pt = svg.createSVGPoint(); pt.x = e.clientX; pt.y = e.clientY;
      const ctm = svg.getScreenCTM();
      const p = ctm ? pt.matrixTransform(ctm.inverse()) : { x: 0, y: 0 };
      // Snap forte: pega o ponto mais próximo dentro do threshold
      const snapThreshold = 20;
      let snapped = { x: p.x, y: p.y, found: false, dist: snapThreshold };
      for (let i = 0; i < pieces.length; i++) {
        const renderPts = getTransformedPoints(pieces[i]);
        if (!renderPts || !renderPts.length) continue;
        for (const [px, py] of renderPts) {
          const dist = Math.sqrt((p.x - px) ** 2 + (p.y - py) ** 2);
          if (dist < snapped.dist) {
            snapped = { x: px, y: py, found: true, dist };
          }
        }
      }

      // Corrigir: garantir que o ponto inicial do retângulo seja sempre o ponto snap
      setRepetRect(prev => {
        if (!prev || !prev.x0 || !prev.y0) {
          // Se for o primeiro movimento, usar o ponto inicial snap
          return { x0: snapped.x, y0: snapped.y, x1: snapped.x, y1: snapped.y };
        }
        return { ...prev, x1: snapped.x, y1: snapped.y };
      });
      // Também garantir que repetStart seja atualizado para o snap no primeiro movimento
      if (!repetStart || typeof repetStart.x !== 'number' || typeof repetStart.y !== 'number') {
        setRepetStart({ x: snapped.x, y: snapped.y });
      }
      // Highlight visual da quina mais próxima durante o movimento
      if (snapped.found) {
        setSnapHighlight({ x: snapped.x, y: snapped.y });
      } else {
        setSnapHighlight(null);
      }
    } else if (repetMode && !isDrawingRepet) {
      // Highlight visual da quina mais próxima ANTES de clicar (modo Repet, mouse parado)
      const svg = svgRef.current; if (!svg) return;
      const pt = svg.createSVGPoint(); pt.x = e.clientX; pt.y = e.clientY;
      const ctm = svg.getScreenCTM();
      const p = ctm ? pt.matrixTransform(ctm.inverse()) : { x: 0, y: 0 };
      const snapThreshold = 40;
      let snapped = { x: p.x, y: p.y, found: false, dist: snapThreshold };
      for (let i = 0; i < pieces.length; i++) {
        const renderPts = getRenderPoints(pieces[i]);
        if (!renderPts || !renderPts.length) continue;
        for (const [px, py] of renderPts) {
          const dist = Math.sqrt((p.x - px) ** 2 + (p.y - py) ** 2);
          if (dist < snapped.dist) {
            snapped = { x: px, y: py, found: true, dist };
          }
        }
      }
      if (snapped.found) {
        setSnapHighlight({ x: snapped.x, y: snapped.y });
      } else {
        setSnapHighlight(null);
      }
    } else if (isSelecting && selectionStart) {
        // Limpa highlight ao iniciar desenho
        setSnapHighlight(null);
      // Seleção por arrasto
      const svg = svgRef.current; if (!svg) return;
      const pt = svg.createSVGPoint(); pt.x = e.clientX; pt.y = e.clientY;
      const ctm = svg.getScreenCTM();
      const p = ctm ? pt.matrixTransform(ctm.inverse()) : { x: 0, y: 0 };
      
      setSelectionBox({ x0: selectionStart.x, y0: selectionStart.y, x1: p.x, y1: p.y });
    }
  }, [repetMode, isDrawingRepet, repetStart, pieces, getTransformedPoints, isSelecting, selectionStart, getRenderPoints]);

  const onSvgMouseUp = useCallback((e) => {
    if (repetMode && isDrawingRepet) {
      // Snap forte no ponto final ao soltar o mouse
      const svg = svgRef.current; if (!svg) {
        setIsDrawingRepet(false);

        return;
      }
      try { e.preventDefault(); e.stopPropagation(); } catch {}
      const pt = svg.createSVGPoint(); pt.x = e.clientX; pt.y = e.clientY;
      const ctm = svg.getScreenCTM();
      const p = ctm ? pt.matrixTransform(ctm.inverse()) : { x: 0, y: 0 };
      const snapThreshold = 20;
      let snapped = { x: p.x, y: p.y, found: false, dist: snapThreshold };
      for (let i = 0; i < pieces.length; i++) {
        const renderPts = getTransformedPoints(pieces[i]);
        if (!renderPts || !renderPts.length) continue;
        for (const [px, py] of renderPts) {
          const dist = Math.sqrt((p.x - px) ** 2 + (p.y - py) ** 2);
          if (dist < snapped.dist) {
            snapped = { x: px, y: py, found: true, dist };
          }
        }
      }

      setRepetRect(prev => prev ? { ...prev, x1: snapped.x, y1: snapped.y } : prev);
      setIsDrawingRepet(false);
    } else if (isSelecting && selectionBox) {
      // Finaliza seleção por arrasto
      const { x0, y0, x1, y1 } = selectionBox;
      const minX = Math.min(x0, x1);
      const maxX = Math.max(x0, x1);
      const minY = Math.min(y0, y1);
      const maxY = Math.max(y0, y1);
      
      // Verifica se houve movimento significativo (não foi apenas um clique)
      const width = Math.abs(x1 - x0);
      const height = Math.abs(y1 - y0);
      const minDragDistance = 5; // pixels mínimos para considerar como arrasto
      
      if (width > minDragDistance || height > minDragDistance) {
        // Seleciona todas as peças cujo bbox intersecta o retângulo de seleção
        const newSelected = new Set();
        pieces.forEach((piece, idx) => {
          const bb = bboxOf(piece);
          const pieceMinX = bb.x;
          const pieceMaxX = bb.x + bb.width;
          const pieceMinY = bb.y;
          const pieceMaxY = bb.y + bb.height;
          
          // Verifica interseção
          if (pieceMaxX >= minX && pieceMinX <= maxX && pieceMaxY >= minY && pieceMinY <= maxY) {
            newSelected.add(idx);
          }
        });
        
        // Atualiza seleção
        if (e.shiftKey) {
          // Shift: adiciona à seleção existente
          setSelectedPieces(prev => {
            const combined = new Set(prev);
            newSelected.forEach(idx => combined.add(idx));
            return combined;
          });
        } else if (e.ctrlKey || e.metaKey) {
          // Ctrl/Cmd: toggle (adiciona novas, remove já selecionadas)
          setSelectedPieces(prev => {
            const toggled = new Set(prev);
            newSelected.forEach(idx => {
              if (toggled.has(idx)) {
                toggled.delete(idx);
              } else {
                toggled.add(idx);
              }
            });
            return toggled;
          });
        } else {
          // Sem modificador: substitui seleção
          setSelectedPieces(newSelected);
          if (newSelected.size === 1) {
            setSelected([...newSelected][0]);
          } else {
            setSelected(null);
          }
        }
        
        // Marca que acabamos de finalizar uma seleção para evitar que handleSvgClick limpe
        setJustFinishedSelection(true);
        setTimeout(() => setJustFinishedSelection(false), 100);
      }
      
      setIsSelecting(false);
      setSelectionBox(null);
      setSelectionStart(null);
    }
  }, [repetMode, isDrawingRepet, isSelecting, selectionBox, pieces, getTransformedPoints]);  const handleUploadDXF = async () => {
    if (!dxfFile) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', dxfFile);
    
    // ✅ Parâmetros obrigatórios do backend Java
    fd.append('quantidadePares', '0');
    fd.append('quantidadePeca', '0');
    fd.append('espacamento', '0');
    fd.append('larguraMaterial', String(config.larguraMaterial || 1000));
    fd.append('alturaMaterial', String(config.comprimentoMaterial || 500));
    fd.append('idFichaTecnica', '0');
    fd.append('estrutura', '');
    fd.append('componentes', '');
    fd.append('tamanho', '');
    fd.append('idMateriaPrima', '0');
    fd.append('tipoCalculo', 'metroLinear');
    fd.append('percentualPerda', '0');
    
    try {
      // Chama o backend Java para processar DXF
      const JAVA_API_BASE = URLManager.getBaseURL();
      const processRes = await fetch(`${JAVA_API_BASE}/api/dxf/processar`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('usuarioToken')}`
        },
        body: fd
      });
      if (!processRes.ok) throw new Error('Falha no upload');
      const data = await processRes.json();

      // Se backend retornar lista de peças, usar diretamente (SEM deduplicação)
      if (Array.isArray(data?.pecas)) {
        setPecasDisponiveis(data.pecas);
        if (data.pecas[0]) carregarPecaNoCanvas(data.pecas[0]);
        return;
      }

      // Formato antigo: apenas entities
      const entities = Array.isArray(data?.entities) ? data.entities : [];
      if (!entities.length) {
        try { console.warn('processar retornou formato inesperado:', data); } catch {}
      }
      const normalized = normalizeCoordinates(entities);
      const centered = centerPieces(normalized);
      setPieces(centered.map(clampPieceToMaterial));
      adjustViewBox(centered);
    } catch (err) {
      console.error('Erro upload DXF', err);
      alert('Erro ao processar DXF');
    } finally { setUploading(false); }
  };

  useEffect(() => {
    if (dxfId) {
      (async () => {
        try {
          const JAVA_API_BASE = URLManager.getBaseURL();
          const r = await fetch(`${JAVA_API_BASE}/api/dxf/entidades/${dxfId}`, { headers: { Authorization: `Bearer ${sessionStorage.getItem('usuarioToken')}` } });
          if (!r.ok) throw new Error('fetch DXF');
          const data = await r.json();
          const normalized = normalizeCoordinates(data.entities || []);
          const centered = centerPieces(normalized);
          setPieces(centered.map(clampPieceToMaterial));
          adjustViewBox(centered);
        } catch (e) { console.error('Erro ao buscar entidades DXF', e); }
      })();
    }
  }, [dxfId, normalizeCoordinates, centerPieces, adjustViewBox, clampPieceToMaterial]);

  // Função para carregar uma peça específica no canvas
  const carregarPecaNoCanvas = useCallback((peca) => {
    if (!peca || !peca.entities) {
      console.warn('Peça inválida ou sem entidades:', peca);
      return;
    }
    
    console.log('🎯 Carregando peça no canvas:', {
      nome: peca.name || peca.nome,
      quantidadeEntities: peca.entities.length
    });
    
    // Normaliza as coordenadas das entidades (SEM deduplicação)
    const entidadesNormalizadas = normalizeCoordinates(peca.entities);
    
    // Calcula o bounding box de todas as entidades da peça
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    entidadesNormalizadas.forEach(ent => {
      const bb = bboxOf(ent);
      minX = Math.min(minX, bb.x);
      minY = Math.min(minY, bb.y);
      maxX = Math.max(maxX, bb.x + bb.width);
      maxY = Math.max(maxY, bb.y + bb.height);
    });
    
    // Cria uma única peça agrupando todos os entities
    const pecaAgrupada = {
      entities: entidadesNormalizadas,
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
      rotation: 0,
      nome: peca.name || peca.nome
    };
    
    // Centraliza a peça no material
    const pecasCentralizadas = centerPieces([pecaAgrupada]);
    
    // IMPORTANTE: Substitui TODAS as peças do canvas pela peça selecionada (como UM grupo)
    setPieces(pecasCentralizadas);
    
    // Ajusta o viewBox para enquadrar a peça
    adjustViewBox(pecasCentralizadas);
    
    // Limpa seleções anteriores
    setSelected(null);
    setSelectedPieces(new Set());
    
    console.log('✅ Peça carregada com sucesso:', entidadesNormalizadas.length, 'entities em 1 peça');
  }, [normalizeCoordinates, centerPieces, adjustViewBox]);

  // Atualizar peças disponíveis quando pecasDoDXF mudar (SEM deduplicação)
  useEffect(() => {
    if (pecasDoDXF && pecasDoDXF.length > 0) {
      console.log('📦 Peças disponíveis atualizadas:', pecasDoDXF.length, 'peças recebidas');
      setPecasDisponiveis(pecasDoDXF);
      
      // Carregar automaticamente a primeira peça no canvas
      if (pecasDoDXF[0]) {
        console.log('🎯 Carregando primeira peça automaticamente');
        setIndexAtual(0);
        carregarPecaNoCanvas(pecasDoDXF[0]);
      }
    }
  }, [pecasDoDXF, carregarPecaNoCanvas]);

  // Sincroniza índice selecionado via props
  useEffect(() => {
    if (pecaSelecionadaIndex !== indexAtual) {
      setIndexAtual(pecaSelecionadaIndex);
      // Carrega a peça selecionada no canvas
      if (pecasDisponiveis && pecasDisponiveis[pecaSelecionadaIndex]) {
        carregarPecaNoCanvas(pecasDisponiveis[pecaSelecionadaIndex]);
      }
    }
  }, [pecaSelecionadaIndex, indexAtual, pecasDisponiveis, carregarPecaNoCanvas]);

  const startDrag = useCallback((e, index) => {
    e.preventDefault();
    e.stopPropagation();

    // Snapshot para desfazer este movimento
    setHistory(prev => {
      const next = [...prev, deepClonePieces(pieces)];
      if (next.length > MAX_HISTORY) next.shift();
      return next;
    });

    // Calcular seleção final sincronamente (para capturar posições iniciais do grupo)
    const newGroupSet = (() => {
      if (e.ctrlKey || e.metaKey) {
        const next = new Set(selectedPieces);
        if (next.has(index)) next.delete(index); else next.add(index);
        return next;
      }
      if (selectedPieces.size > 1 && selectedPieces.has(index)) return new Set(selectedPieces);
      return new Set([index]);
    })();
    setSelectedPieces(newGroupSet);
    setSelected(index);
    setDragging(index);
    const svg = svgRef.current; if (!svg) return;
    let mouseX = 0, mouseY = 0;
    try { const pt = svg.createSVGPoint(); pt.x = e.clientX; pt.y = e.clientY; const ctm = svg.getScreenCTM(); const svgP = pt.matrixTransform(ctm.inverse()); mouseX = svgP.x; mouseY = svgP.y; }
    catch (err) { const rect = svg.getBoundingClientRect(); mouseX = e.clientX - rect.left; mouseY = e.clientY - rect.top; }
    const piece = pieces[index];
    const pieceX = (piece.x != null) ? piece.x : (piece.center ? piece.center[0] - piece.radius : 0);
    const pieceY = (piece.y != null) ? piece.y : (piece.center ? piece.center[1] - piece.radius : 0);
    setDragOffset({ x: mouseX - pieceX, y: mouseY - pieceY });
    // Guardar posições iniciais das peças selecionadas para mover o grupo a partir delas
    try {
      const originals = {};
      newGroupSet.forEach(i => {
        if (pieces[i]) originals[i] = JSON.parse(JSON.stringify(pieces[i]));
      });
      dragStartRef.current = { mouseX, mouseY, originals };
    } catch (err) { dragStartRef.current = { mouseX, mouseY, originals: {} }; }
  }, [pieces, deepClonePieces, selectedPieces]);

  // Ctrl+Z para desfazer último movimento
  useEffect(() => {
    const onKeyDown = (e) => {
      const tag = (e.target && e.target.tagName) ? e.target.tagName.toUpperCase() : '';
      const isEditable = (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (e.target && e.target.isContentEditable));
      if (isEditable) return;
      const isUndo = (e.ctrlKey || e.metaKey) && (e.key === 'z' || e.key === 'Z');
      if (!isUndo) return;
      if (!history.length) return;
      try { e.preventDefault(); } catch {}
      const last = history[history.length - 1];
      setPieces(last);
      setHistory(prev => prev.slice(0, prev.length - 1));
      setDragging(null);
      setSelected(null);
      // mantém seleção múltipla inalterada
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [history]);

  const onMouseMoveWindow = useCallback((e) => {
    if (dragging === null) return;
    const svg = svgRef.current; if (!svg) return;
    let mouseX = 0, mouseY = 0;
    try {
      const pt = svg.createSVGPoint(); pt.x = e.clientX; pt.y = e.clientY; const ctm = svg.getScreenCTM(); const svgP = pt.matrixTransform(ctm.inverse());
      mouseX = svgP.x; mouseY = svgP.y;
    } catch (err) {
      const rect = svg.getBoundingClientRect(); mouseX = e.clientX - rect.left; mouseY = e.clientY - rect.top;
    }
    // If Alt is held while moving, temporarily override spacing to 0 to allow tight fitting
    try { spacingOverrideRef.current = e && e.altKey ? 0 : null; } catch (err) { spacingOverrideRef.current = null; }
    const nx = mouseX - dragOffset.x; const ny = mouseY - dragOffset.y;
    // Guardar última posição e agendar um frame
    moveRafRef.current.x = nx; moveRafRef.current.y = ny;
    if (moveRafRef.current.scheduled) return;
    moveRafRef.current.scheduled = true;
    moveRafRef.current.id = requestAnimationFrame(() => {
      const newX = moveRafRef.current.x; const newY = moveRafRef.current.y;
      setPieces(prev => {
        const groupSet = selectedPieces.size > 1 ? new Set(selectedPieces) : new Set([dragging]);
        const mainOriginal = prev[dragging];
        if (!mainOriginal) return prev;

        // Caso de seleção múltipla: mover grupo rigidamente e CLAMPAR o GRUPO (evita "puxar" peças individualmente e jitter)
        if (groupSet.size > 1) {
          // Novo: mover grupo a partir das posições iniciais armazenadas em dragStartRef
          const start = dragStartRef.current;
          if (!start || !start.originals) return prev;

          // Calcular delta em pixels baseado na posição original da peça principal
          const origMain = (start.originals && start.originals[dragging]) ? start.originals[dragging] : null;
          const origMainX = origMain && origMain.x != null ? origMain.x : (origMain && origMain.center ? (origMain.center[0] - (origMain.radius || 0)) : null);
          const origMainY = origMain && origMain.y != null ? origMain.y : (origMain && origMain.center ? (origMain.center[1] - (origMain.radius || 0)) : null);
          const ddx = (origMainX != null) ? (newX - origMainX) : (newX - (start.mouseX || 0));
          const ddy = (origMainY != null) ? (newY - origMainY) : (newY - (start.mouseY || 0));

          // Determine dominant movement axis for group (if any) to avoid drifting
          const allowedAxisGroup = (Math.abs(ddx) > Math.abs(ddy) * 1.2) ? 'x' : (Math.abs(ddy) > Math.abs(ddx) * 1.2 ? 'y' : null);

          // Aplicar delta a cada peça baseada na cópia original guardada
          let candidates = prev.map((p, i) => {
            if (!groupSet.has(i)) return p;
            const orig = start.originals[i] || p;
            return deltaMovePiece(JSON.parse(JSON.stringify(orig)), ddx, ddy);
          });

          // Clampar o grupo inteiro às margens (usando bounds reais)
          const inner = getInnerBounds();
          let gMinX = Infinity, gMinY = Infinity, gMaxX = -Infinity, gMaxY = -Infinity;
          candidates.forEach((p, i) => {
            if (!groupSet.has(i)) return;
            const r = getRealDimensions(p);
            gMinX = Math.min(gMinX, r.minX);
            gMinY = Math.min(gMinY, r.minY);
            gMaxX = Math.max(gMaxX, r.maxX);
            gMaxY = Math.max(gMaxY, r.maxY);
          });
          let corrX = 0, corrY = 0;
          if (gMinX < inner.xMin) corrX += (inner.xMin - gMinX);
          if (gMaxX > inner.xMax) corrX += (inner.xMax - gMaxX);
          if (gMinY < inner.yMin) corrY += (inner.yMin - gMinY);
          if (gMaxY > inner.yMax) corrY += (inner.yMax - gMaxY);
          if (corrX !== 0 || corrY !== 0) {
            candidates = candidates.map((p, idx) => groupSet.has(idx) ? deltaMovePiece(p, corrX, corrY) : p);
          }

          // Agora garantir que o grupo não colida com peças externas respeitando spacing
          const external = prev.filter((_, idx) => !groupSet.has(idx));
          const spacingPx = getSpacingPx();

          const anyCollisionBetween = (candArr, extArr) => {
            for (const cp of candArr) {
              for (const ep of extArr) {
                const coll = fastCollisionRef.current ? detectCollisionWithSpacing(cp, ep, spacingPx) : detectCollisionWithExactPoints(cp, ep, spacingPx);
                if (coll) return true;
              }
            }
            return false;
          };

          // If there is collision, try to gently separate the whole group away from colliding externals
          let attempts = 0;
          const maxAttempts = 200;
          while (anyCollisionBetween(candidates.filter((_,i)=>groupSet.has(i)), external) && attempts < maxAttempts) {
            // accumulate separation directions from colliding pairs
            let dxSum = 0, dySum = 0, cnt = 0;
            for (let i = 0; i < candidates.length; i++) {
              if (!groupSet.has(i)) continue;
              const cp = candidates[i];
              for (const ep of external) {
                const coll = fastCollisionRef.current ? detectCollisionWithSpacing(cp, ep, spacingPx) : detectCollisionWithExactPoints(cp, ep, spacingPx);
                if (coll) {
                  const dir = findSeparationDirection(cp, ep);
                  dxSum += dir.x; dySum += dir.y; cnt++;
                }
              }
            }
            if (cnt === 0) break;
            const mag = Math.sqrt(dxSum * dxSum + dySum * dySum) || 1;
            // Use a small 1px step to avoid overshooting when separating groups.
            // Using spacingPx/2 caused large jumps when spacing is big; 1px lets the loop converge precisely.
            const step = 1;
            let stepX = (dxSum / mag) * step;
            let stepY = (dySum / mag) * step;
            if (allowedAxisGroup === 'x') stepY = 0;
            if (allowedAxisGroup === 'y') stepX = 0;

            candidates = candidates.map((p, idx) => groupSet.has(idx) ? clampPieceToMaterial(deltaMovePiece(p, stepX, stepY)) : p);
            attempts++;
          }

          // After separation, compute a single group translation that snaps the group
          // when any group piece is within SNAP_PX of an external piece or material margin.
          const inner2 = inner; // reuse inner bounds
          const spacingPx2 = spacingPx;
          const groupIndices = Array.from(groupSet);
          const candidateTranslations = [];

          // If automatic approximation is disabled, skip snap calculations for performance.
          if (!config.aproximacaoAutomatica) {
            return candidates.map((p, idx) => groupSet.has(idx) ? clampPieceToMaterial(p) : p);
          }

          // Check snaps against material margins
          for (const gi of groupIndices) {
            const gp = candidates[gi];
            const rP = getRealDimensions(gp);
            // left margin
            if (Math.abs(rP.minX - inner2.xMin) <= SNAP_PX) {
              candidateTranslations.push({ dx: inner2.xMin - rP.minX, dy: 0, weight: Math.abs(inner2.xMin - rP.minX) });
            }
            // right margin
            if (Math.abs(rP.maxX - inner2.xMax) <= SNAP_PX) {
              candidateTranslations.push({ dx: inner2.xMax - rP.maxX, dy: 0, weight: Math.abs(inner2.xMax - rP.maxX) });
            }
            // top/bottom
            if (Math.abs(rP.minY - inner2.yMin) <= SNAP_PX) {
              candidateTranslations.push({ dx: 0, dy: inner2.yMin - rP.minY, weight: Math.abs(inner2.yMin - rP.minY) });
            }
            if (Math.abs(rP.maxY - inner2.yMax) <= SNAP_PX) {
              candidateTranslations.push({ dx: 0, dy: inner2.yMax - rP.maxY, weight: Math.abs(inner2.yMax - rP.maxY) });
            }
          }

          // Check snaps against external pieces (respect spacing)
          for (const gi of groupIndices) {
            const gp = candidates[gi];
            const rP = getRealDimensions(gp);
            for (const ep of external) {
              const rE = getRealDimensions(ep);
              // gp right to external left
              const targetRight = rE.minX - spacingPx2;
              if (Math.abs(rP.maxX - targetRight) <= SNAP_PX) {
                candidateTranslations.push({ dx: targetRight - rP.maxX, dy: 0, weight: Math.abs(targetRight - rP.maxX) });
              }
              // gp left to external right
              const targetLeft = rE.maxX + spacingPx2;
              if (Math.abs(rP.minX - targetLeft) <= SNAP_PX) {
                candidateTranslations.push({ dx: targetLeft - rP.minX, dy: 0, weight: Math.abs(targetLeft - rP.minX) });
              }
              // gp bottom to external top
              const targetBottom = rE.minY - spacingPx2;
              if (Math.abs(rP.maxY - targetBottom) <= SNAP_PX) {
                candidateTranslations.push({ dx: 0, dy: targetBottom - rP.maxY, weight: Math.abs(targetBottom - rP.maxY) });
              }
              // gp top to external bottom
              const targetTop = rE.maxY + spacingPx2;
              if (Math.abs(rP.minY - targetTop) <= SNAP_PX) {
                candidateTranslations.push({ dx: 0, dy: targetTop - rP.minY, weight: Math.abs(targetTop - rP.minY) });
              }
            }
          }

          if (candidateTranslations.length > 0) {
            // choose the smallest translation (by weight) to snap precisely to nearest contact
            candidateTranslations.sort((a, b) => a.weight - b.weight);
            const best = candidateTranslations[0];
            // Apply same translation to whole group
            candidates = candidates.map((p, idx) => groupSet.has(idx) ? deltaMovePiece(p, best.dx, best.dy) : p);
            // Ensure clamping to material
            candidates = candidates.map((p, idx) => groupSet.has(idx) ? clampPieceToMaterial(p) : p);
          }

          return candidates;
        }

        // Caso de seleção única: comportamento anterior (snap e espaçamento contra externos)
        const external = prev.filter((_, idx) => !groupSet.has(idx));
        let mainCandidate = { ...mainOriginal };
        // compute allowedAxis outside branches so it's in scope for clampPieceSpacing
        let allowedAxis = null;
        if (mainOriginal.points && mainOriginal.points.length) {
          const dx0 = newX - (mainOriginal.x || 0); const dy0 = newY - (mainOriginal.y || 0);
          allowedAxis = (Math.abs(dx0) > Math.abs(dy0) * 1.2) ? 'x' : (Math.abs(dy0) > Math.abs(dx0) * 1.2 ? 'y' : null);
          mainCandidate.points = mainOriginal.points.map(pt => Array.isArray(pt) ? [pt[0] + dx0, pt[1] + dy0] : { x: pt.x + dx0, y: pt.y + dy0 });
          const bb = bboxOf({ points: mainCandidate.points }); mainCandidate.x = bb.x; mainCandidate.y = bb.y; mainCandidate.width = bb.width; mainCandidate.height = bb.height;
        } else if (mainOriginal.center && mainOriginal.radius != null) {
          mainCandidate.center = [newX + mainOriginal.radius, newY + mainOriginal.radius];
          mainCandidate.x = newX; mainCandidate.y = newY;
          const dx0 = newX - (mainOriginal.x || 0); const dy0 = newY - (mainOriginal.y || 0);
          allowedAxis = (Math.abs(dx0) > Math.abs(dy0) * 1.2) ? 'x' : (Math.abs(dy0) > Math.abs(dx0) * 1.2 ? 'y' : null);
        } else {
          mainCandidate.x = newX; mainCandidate.y = newY;
          const dx0 = newX - (mainOriginal.x || 0); const dy0 = newY - (mainOriginal.y || 0);
          allowedAxis = (Math.abs(dx0) > Math.abs(dy0) * 1.2) ? 'x' : (Math.abs(dy0) > Math.abs(dx0) * 1.2 ? 'y' : null);
        }
        mainCandidate = clampPieceToMaterial(mainCandidate);
        mainCandidate = clampPieceSpacing(mainCandidate, -1, external, allowedAxis);
        // Only apply snap when automatic approximation is enabled
        if (config.aproximacaoAutomatica) {
          mainCandidate = applySnap(mainCandidate, -1, external);
        }

        return prev.map((p, i) => (i === dragging ? mainCandidate : p));
      });
      moveRafRef.current.scheduled = false; moveRafRef.current.id = 0;
    });
  }, [dragging, dragOffset, detectCollisionWithSpacing, applySnap, clampPieceToMaterial, clampPieceSpacing, selectedPieces, getInnerBounds, getRealDimensions, getSpacingPx, deltaMovePiece, detectCollisionWithExactPoints, findSeparationDirection, config.aproximacaoAutomatica]);

  useEffect(() => {
    if (dragging !== null) {
      window.addEventListener('mousemove', onMouseMoveWindow);
      window.addEventListener('mouseup', () => setDragging(null));
    }
    return () => { 
      // ensure temporary spacing override is cleared when drag stops/cleanup
      try { spacingOverrideRef.current = null; } catch (err) { spacingOverrideRef.current = null; }
      window.removeEventListener('mousemove', onMouseMoveWindow); window.removeEventListener('mouseup', () => setDragging(null));
    };
  }, [dragging, onMouseMoveWindow]);

  const rotatePiece = useCallback((index, angle) => {
    setPieces(prev => prev.map((p, i) => {
      if (i !== index) return p;
      
      console.log('🔄 rotatePiece - Peça antes:', {
        hasEntities: !!p.entities,
        entitiesCount: p.entities?.length,
        rotation: p.rotation,
        x: p.x,
        y: p.y,
        width: p.width,
        height: p.height
      });
      
      const inner = getInnerBounds();
      const realBefore = getRealDimensions(p);
      const tol = SNAP_PX + 1;
      const wasLeftAnchored = Math.abs(realBefore.minX - inner.xMin) <= tol;
      const wasRightAnchored = Math.abs(realBefore.maxX - inner.xMax) <= tol;
      const wasTopAnchored = Math.abs(realBefore.minY - inner.yMin) <= tol;
      const wasBottomAnchored = Math.abs(realBefore.maxY - inner.yMax) <= tol;
      const newRot = ((p.rotation || 0) + angle) % 360;
      let candidate = { ...p, rotation: newRot };

      // Para peças com entities, APENAS atualizar o rotation (não modificar points dos entities)
      if (p.entities && Array.isArray(p.entities) && p.entities.length > 0) {
        // Não precisa fazer nada além de atualizar rotation
        // Os entities mantêm suas coordenadas originais
        console.log('✅ rotatePiece - Peça com entities, nova rotação:', newRot);
      } else if (p.points && p.points.length) {
        // Para peças antigas (single entity com points), rotacionar os pontos
        const [cx, cy] = getRotationCenter(p);
        const pts = getPiecePoints(p);
        const rotated = rotatePoints(pts, cx, cy, angle);
        candidate.points = rotated.map(([x, y]) => [x, y]);
        const bb = bboxOf({ points: candidate.points });
        candidate.x = bb.x; candidate.y = bb.y; candidate.width = bb.width; candidate.height = bb.height;
      }

      // Re-ancorar nas margens, se já estava ancorado antes (usando contorno REAL)
      if (wasLeftAnchored) candidate = movePieceRealMinTo(candidate, inner.xMin, getRealDimensions(candidate).minY);
      if (wasTopAnchored) candidate = movePieceRealMinTo(candidate, getRealDimensions(candidate).minX, inner.yMin);
      if (wasRightAnchored) candidate = movePieceRealMaxTo(candidate, inner.xMax, getRealDimensions(candidate).maxY);
      if (wasBottomAnchored) candidate = movePieceRealMaxTo(candidate, getRealDimensions(candidate).maxX, inner.yMax);

      // Clamping ao material sempre
      candidate = clampPieceToMaterial(candidate);

      // Se permitir sobreposição, pular checagens/correções de colisão/espaçamento e snap com outras peças
      if (!config.permitirSobreposicao) {
        const anchorFlags = { left: wasLeftAnchored, right: wasRightAnchored, top: wasTopAnchored, bottom: wasBottomAnchored };
        if (wasLeftAnchored || wasRightAnchored || wasTopAnchored || wasBottomAnchored) {
          const others = prev.filter((_, idx) => idx !== i);
          candidate = resolveCollisionsWithAnchor(candidate, others, anchorFlags, inner);
        } else {
          candidate = clampPieceSpacing(candidate, i, prev);
        }

        // Reforçar âncoras após ajustes
        if (wasLeftAnchored) candidate = movePieceRealMinTo(candidate, inner.xMin, getRealDimensions(candidate).minY);
        if (wasTopAnchored) candidate = movePieceRealMinTo(candidate, getRealDimensions(candidate).minX, inner.yMin);
        if (wasRightAnchored) candidate = movePieceRealMaxTo(candidate, inner.xMax, getRealDimensions(candidate).maxY);
        if (wasBottomAnchored) candidate = movePieceRealMaxTo(candidate, getRealDimensions(candidate).maxX, inner.yMax);

        // Snap apenas se não estava ancorado
        if (!(wasLeftAnchored || wasRightAnchored || wasTopAnchored || wasBottomAnchored)) {
          candidate = applySnap(candidate, i, prev);
        }

        if (config.giraAoAproximar) {
          const spacingPx = getSpacingPx();
          const hasCollision = prev.some((other, idx) => idx !== i && detectCollisionWithExactPoints(candidate, other, spacingPx));
          if (hasCollision) {
            const adjustments = [
              [spacingPx, 0], [0, spacingPx], [-spacingPx, 0], [0, -spacingPx],
              [spacingPx, spacingPx], [-spacingPx, -spacingPx]
            ];
            for (const [adjX, adjY] of adjustments) {
              let adjusted = deltaMovePiece(candidate, adjX, adjY);
              adjusted = clampPieceToMaterial(adjusted);
              adjusted = clampPieceSpacing(adjusted, i, prev);
              const stillCollision = prev.some((other, idx) => idx !== i && detectCollisionWithExactPoints(adjusted, other, spacingPx));
              if (!stillCollision) return adjusted;
            }
          }
        }
      }

      return candidate;
    }));
  }, [
    getInnerBounds,
    getRealDimensions,
    getRotationCenter,
    getPiecePoints,
    rotatePoints,
    getSpacingPx,
    deltaMovePiece,
    clampPieceToMaterial,
    clampPieceSpacing,
    applySnap,
    detectCollisionWithExactPoints,
    movePieceRealMinTo,
    movePieceRealMaxTo,
    resolveCollisionsWithAnchor,
    config.giraAoAproximar,
    config.permitirSobreposicao
  ]);

  // Rotacionar múltiplas peças selecionadas em torno do centro do grupo
  const rotatePiecesInPlace = useCallback((indicesSet, angle) => {
    setPieces(prev => {
      // 1. Calcular centro do grupo
      const selected = Array.from(indicesSet).map(idx => prev[idx]).filter(Boolean);
      if (selected.length === 0) return prev;
      // Usar centroide dos centros das peças
      const centers = selected.map(p => {
        if (p.entities && Array.isArray(p.entities) && p.entities.length > 0) {
          // Centro local dos entities
          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
          p.entities.forEach(entity => {
            if (entity.points && entity.points.length) {
              entity.points.forEach(pt => {
                const x = Array.isArray(pt) ? pt[0] : (pt.x ?? pt[0]);
                const y = Array.isArray(pt) ? pt[1] : (pt.y ?? pt[1]);
                if (x < minX) minX = x;
                if (y < minY) minY = y;
                if (x > maxX) maxX = x;
                if (y > maxY) maxY = y;
              });
            }
          });
          const centerX = minX + (maxX - minX) / 2;
          const centerY = minY + (maxY - minY) / 2;
          return [ (p.x || 0) + centerX, (p.y || 0) + centerY ];
        } else if (p.points && p.points.length) {
          // Centroide dos pontos
          const xs = p.points.map(pt => Array.isArray(pt) ? pt[0] : pt.x);
          const ys = p.points.map(pt => Array.isArray(pt) ? pt[1] : pt.y);
          const cx = xs.reduce((a, b) => a + b, 0) / xs.length;
          const cy = ys.reduce((a, b) => a + b, 0) / ys.length;
          return [cx, cy];
        } else if (p.center) {
          return p.center;
        } else {
          return [p.x || 0, p.y || 0];
        }
      });
      const groupCenter = [
        centers.reduce((a, b) => a + b[0], 0) / centers.length,
        centers.reduce((a, b) => a + b[1], 0) / centers.length
      ];

      // 2. Rotacionar cada peça em torno do centro do grupo
      return prev.map((p, i) => {
        if (!indicesSet.has(i)) return p;
        let candidate = { ...p };
        // Rotação não altera círculos
        if (p.type === 'CIRCLE' && p.center && p.radius != null) {
          return candidate;
        }

        // Calcular centro da peça
        let pieceCenter;
        if (p.entities && Array.isArray(p.entities) && p.entities.length > 0) {
          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
          p.entities.forEach(entity => {
            if (entity.points && entity.points.length) {
              entity.points.forEach(pt => {
                const x = Array.isArray(pt) ? pt[0] : (pt.x ?? pt[0]);
                const y = Array.isArray(pt) ? pt[1] : (pt.y ?? pt[1]);
                if (x < minX) minX = x;
                if (y < minY) minY = y;
                if (x > maxX) maxX = x;
                if (y > maxY) maxY = y;
              });
            }
          });
          const centerX = minX + (maxX - minX) / 2;
          const centerY = minY + (maxY - minY) / 2;
          pieceCenter = [ (p.x || 0) + centerX, (p.y || 0) + centerY ];
        } else if (p.points && p.points.length) {
          const xs = p.points.map(pt => Array.isArray(pt) ? pt[0] : pt.x);
          const ys = p.points.map(pt => Array.isArray(pt) ? pt[1] : pt.y);
          const cx = xs.reduce((a, b) => a + b, 0) / xs.length;
          const cy = ys.reduce((a, b) => a + b, 0) / ys.length;
          pieceCenter = [cx, cy];
        } else if (p.center) {
          pieceCenter = p.center;
        } else {
          pieceCenter = [p.x || 0, p.y || 0];
        }

        // Vetor do centro do grupo até o centro da peça
        const dx = pieceCenter[0] - groupCenter[0];
        const dy = pieceCenter[1] - groupCenter[1];
        const rad = (angle * Math.PI) / 180;
        // Novo centro após rotação
        const newDx = dx * Math.cos(rad) - dy * Math.sin(rad);
        const newDy = dx * Math.sin(rad) + dy * Math.cos(rad);
        const newCenter = [groupCenter[0] + newDx, groupCenter[1] + newDy];

        // Atualizar posição da peça para manter formação
        if (p.entities && Array.isArray(p.entities) && p.entities.length > 0) {
          // Atualizar x/y mantendo o centro local
          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
          p.entities.forEach(entity => {
            if (entity.points && entity.points.length) {
              entity.points.forEach(pt => {
                const x = Array.isArray(pt) ? pt[0] : (pt.x ?? pt[0]);
                const y = Array.isArray(pt) ? pt[1] : (pt.y ?? pt[1]);
                if (x < minX) minX = x;
                if (y < minY) minY = y;
                if (x > maxX) maxX = x;
                if (y > maxY) maxY = y;
              });
            }
          });
          const centerX = minX + (maxX - minX) / 2;
          const centerY = minY + (maxY - minY) / 2;
          candidate.x = newCenter[0] - centerX;
          candidate.y = newCenter[1] - centerY;
          candidate.rotation = ((p.rotation || 0) + angle) % 360;
        } else if (p.points && p.points.length) {
          // Rotacionar pontos em torno do centro do grupo
          const pts = p.points.map(pt => Array.isArray(pt) ? pt : [pt.x, pt.y]);
          const rotated = pts.map(([x, y]) => {
            const dx = x - groupCenter[0];
            const dy = y - groupCenter[1];
            const rx = dx * Math.cos(rad) - dy * Math.sin(rad) + groupCenter[0];
            const ry = dx * Math.sin(rad) + dy * Math.cos(rad) + groupCenter[1];
            return [rx, ry];
          });
          candidate.points = rotated;
          const bb = bboxOf({ points: candidate.points });
          candidate.x = bb.x; candidate.y = bb.y; candidate.width = bb.width; candidate.height = bb.height;
          candidate.rotation = ((p.rotation || 0) + angle) % 360;
        } else if (p.center) {
          // Para círculos, só atualiza o centro
          candidate.center = newCenter;
        } else {
          candidate.x = newCenter[0];
          candidate.y = newCenter[1];
        }

        // Garantir apenas que permaneça dentro da área útil
        candidate = clampPieceToMaterial(candidate);
        return candidate;
      });
    });
  }, [clampPieceToMaterial]);

  // Espelhar peça (horizontal por padrão). Mantém âncoras e aplica clamp/spacing/snap
  const mirrorPiece = useCallback((index, axis = 'horizontal') => {
    setPieces(prev => prev.map((p, i) => {
      if (i !== index) return p;
      const inner = getInnerBounds();
      const realBefore = getRealDimensions(p);
      const tol = SNAP_PX + 1;
      const wasLeftAnchored = Math.abs(realBefore.minX - inner.xMin) <= tol;
      const wasRightAnchored = Math.abs(realBefore.maxX - inner.xMax) <= tol;
      const wasTopAnchored = Math.abs(realBefore.minY - inner.yMin) <= tol;
      const wasBottomAnchored = Math.abs(realBefore.maxY - inner.yMax) <= tol;

      let candidate = { ...p };
      // Usar a geometria renderizada (inclui rotação atual) para garantir que o espelho mantenha a orientação visível
      const rbb = rotatedBBoxFromPiece(p, getPiecePoints, getRotationCenter, rotatePoints, getTransformedPoints);
      const cx = rbb.x + rbb.width / 2;
      const cy = rbb.y + rbb.height / 2;
      const renderPts = getRenderPoints(p);
      const mirrorPts = (pts) => (pts || []).map(([x, y]) => (
        axis === 'vertical' ? [x, 2 * cy - y] : [2 * cx - x, y]
      ));
      const mirrored = mirrorPts(renderPts);
      candidate.points = mirrored.map(([x, y]) => [x, y]);
      const bbNew = bboxOf({ points: candidate.points });
      candidate.x = bbNew.x; candidate.y = bbNew.y; candidate.width = bbNew.width; candidate.height = bbNew.height;
      // Zerar rotação pois já “assamos” a orientação nos pontos espelhados
      candidate.rotation = 0;

      // Re-ancorar se estava ancorado
      if (wasLeftAnchored) candidate = movePieceRealMinTo(candidate, inner.xMin, getRealDimensions(candidate).minY);
      if (wasTopAnchored) candidate = movePieceRealMinTo(candidate, getRealDimensions(candidate).minX, inner.yMin);
      if (wasRightAnchored) candidate = movePieceRealMaxTo(candidate, inner.xMax, getRealDimensions(candidate).maxY);
      if (wasBottomAnchored) candidate = movePieceRealMaxTo(candidate, getRealDimensions(candidate).maxX, inner.yMax);

      candidate = clampPieceToMaterial(candidate);
      if (!config.permitirSobreposicao) {
        const anchorFlags = { left: wasLeftAnchored, right: wasRightAnchored, top: wasTopAnchored, bottom: wasBottomAnchored };
        if (wasLeftAnchored || wasRightAnchored || wasTopAnchored || wasBottomAnchored) {
          const others = prev.filter((_, idx) => idx !== i);
          candidate = resolveCollisionsWithAnchor(candidate, others, anchorFlags, inner);
        } else {
          candidate = clampPieceSpacing(candidate, i, prev);
        }

        if (wasLeftAnchored) candidate = movePieceRealMinTo(candidate, inner.xMin, getRealDimensions(candidate).minY);
        if (wasTopAnchored) candidate = movePieceRealMinTo(candidate, getRealDimensions(candidate).minX, inner.yMin);
        if (wasRightAnchored) candidate = movePieceRealMaxTo(candidate, inner.xMax, getRealDimensions(candidate).maxY);
        if (wasBottomAnchored) candidate = movePieceRealMaxTo(candidate, getRealDimensions(candidate).maxX, inner.yMax);

        // Snap apenas se não estava ancorado
        if (!(wasLeftAnchored || wasRightAnchored || wasTopAnchored || wasBottomAnchored)) {
          candidate = applySnap(candidate, i, prev);
        }
      }

      return candidate;
    }));
  }, [
    getInnerBounds,
    getRealDimensions,
    getPiecePoints,
    getRotationCenter,
    rotatePoints,
    getTransformedPoints,
    rotatedBBoxFromPiece,
    getRenderPoints,
    movePieceRealMinTo,
    movePieceRealMaxTo,
    clampPieceToMaterial,
    clampPieceSpacing,
    applySnap,
    resolveCollisionsWithAnchor,
    config.permitirSobreposicao
  ]);

  // Alinhar peça automaticamente - usa PCA (Principal Component Analysis) para encontrar orientação ideal
  const alignPiece = useCallback((index) => {
    setPieces(prev => prev.map((p, i) => {
      if (i !== index) return p;
      
      // Obter pontos renderizados (com rotação atual aplicada)
      const renderPts = getRenderPoints(p);
      if (!renderPts || renderPts.length < 3) return p;

      // Calcular centroide dos pontos
      let sumX = 0, sumY = 0;
      renderPts.forEach(([x, y]) => {
        sumX += x;
        sumY += y;
      });
      const centroidX = sumX / renderPts.length;
      const centroidY = sumY / renderPts.length;

      // Calcular matriz de covariância
      let cxx = 0, cxy = 0, cyy = 0;
      renderPts.forEach(([x, y]) => {
        const dx = x - centroidX;
        const dy = y - centroidY;
        cxx += dx * dx;
        cxy += dx * dy;
        cyy += dy * dy;
      });

      // Normalizar pela quantidade de pontos
      cxx /= renderPts.length;
      cxy /= renderPts.length;
      cyy /= renderPts.length;

      // Calcular autovalores e autovetores (PCA)
      // O autovetor principal indica a direção de maior variância (direção principal da peça)
      const trace = cxx + cyy;
      const det = cxx * cyy - cxy * cxy;
      const discriminant = Math.sqrt(trace * trace / 4 - det);
      
      const eigenvalue1 = trace / 2 + discriminant;
      // const eigenvalue2 = trace / 2 - discriminant; // Não utilizado no cálculo atual

      // Autovetor associado ao maior autovalor (direção principal)
      let principalAngle;
      if (Math.abs(cxy) > 1e-10) {
        const vx = eigenvalue1 - cyy;
        const vy = cxy;
        principalAngle = Math.atan2(vy, vx) * 180 / Math.PI;
      } else {
        // Matriz diagonal - usar a maior variância
        principalAngle = cxx > cyy ? 0 : 90;
      }

      // Normalizar ângulo para [0, 180)
      principalAngle = ((principalAngle % 180) + 180) % 180;

      // Método alternativo: análise de bordas longas para validação
      const segments = [];
      let maxSegmentLength = 0;
      
      for (let j = 0; j < renderPts.length; j++) {
        const p1 = renderPts[j];
        const p2 = renderPts[(j + 1) % renderPts.length];
        const dx = p2[0] - p1[0];
        const dy = p2[1] - p1[1];
        const length = Math.sqrt(dx * dx + dy * dy);
        
        if (length > maxSegmentLength) maxSegmentLength = length;
        
        if (length > 1) {
          const angle = Math.atan2(dy, dx) * 180 / Math.PI;
          const normalizedAngle = ((angle % 180) + 180) % 180;
          segments.push({ length, angle: normalizedAngle });
        }
      }

      // Considerar apenas segmentos significativos (>20% do maior)
      const significantSegments = segments.filter(seg => seg.length > maxSegmentLength * 0.2);
      
      // Se houver segmentos muito dominantes, usá-los
      let edgeBasedAngle = principalAngle;
      if (significantSegments.length > 0) {
        const totalLength = significantSegments.reduce((sum, seg) => sum + seg.length, 0);
        edgeBasedAngle = significantSegments.reduce((sum, seg) => 
          sum + seg.angle * seg.length, 0) / totalLength;
      }

      // Combinar ambos os métodos com peso maior para PCA
      let combinedAngle = (principalAngle * 0.7 + edgeBasedAngle * 0.3);

      // Encontrar o ângulo de referência mais próximo: 0° ou 90°
      // (180° é equivalente a 0° no intervalo [0, 180))
      let closestReference = 0;
      const diff0 = Math.min(Math.abs(combinedAngle - 0), Math.abs(combinedAngle - 180));
      const diff90 = Math.abs(combinedAngle - 90);
      
      if (diff90 < diff0) {
        closestReference = 90;
      }

      // Calcular a correção necessária
      let correctionAngle = closestReference - combinedAngle;
      
      // Ajustar para o menor caminho
      if (correctionAngle > 90) correctionAngle -= 180;
      if (correctionAngle < -90) correctionAngle += 180;

      // Se a correção for muito pequena (< 0.01°), não fazer nada
      if (Math.abs(correctionAngle) < 0.01) return p;

      // Aplicar a correção
      const inner = getInnerBounds();
      const realBefore = getRealDimensions(p);
      const tol = SNAP_PX + 1;
      const wasLeftAnchored = Math.abs(realBefore.minX - inner.xMin) <= tol;
      const wasRightAnchored = Math.abs(realBefore.maxX - inner.xMax) <= tol;
      const wasTopAnchored = Math.abs(realBefore.minY - inner.yMin) <= tol;
      const wasBottomAnchored = Math.abs(realBefore.maxY - inner.yMax) <= tol;

      let candidate = { ...p };
      const [cx, cy] = getRotationCenter(p);
      const pts = getPiecePoints(p);
      
      if (pts && pts.length) {
        const rotated = rotatePoints(pts, cx, cy, correctionAngle);
        candidate.points = rotated.map(([x, y]) => [x, y]);
        const bb = bboxOf({ points: candidate.points });
        candidate.x = bb.x;
        candidate.y = bb.y;
        candidate.width = bb.width;
        candidate.height = bb.height;
      }
      
      // Definir rotação final como 0
      candidate.rotation = 0;

      // Re-ancorar se estava ancorado
      if (wasLeftAnchored) candidate = movePieceRealMinTo(candidate, inner.xMin, getRealDimensions(candidate).minY);
      if (wasTopAnchored) candidate = movePieceRealMinTo(candidate, getRealDimensions(candidate).minX, inner.yMin);
      if (wasRightAnchored) candidate = movePieceRealMaxTo(candidate, inner.xMax, getRealDimensions(candidate).maxY);
      if (wasBottomAnchored) candidate = movePieceRealMaxTo(candidate, getRealDimensions(candidate).maxX, inner.yMax);

      candidate = clampPieceToMaterial(candidate);

      return candidate;
    }));
  }, [
    getRenderPoints,
    getRotationCenter,
    getPiecePoints,
    rotatePoints,
    getInnerBounds,
    getRealDimensions,
    movePieceRealMinTo,
    movePieceRealMaxTo,
    clampPieceToMaterial
  ]);

  // Alinhar múltiplas peças selecionadas
  const alignSelectedPieces = useCallback((indicesSet) => {
    const indices = Array.from(indicesSet);
    indices.forEach(idx => alignPiece(idx));
  }, [alignPiece]);

  // Debug do bounding box (opcional)
        // eslint-disable-next-line no-unused-vars
        const renderBoundingBoxDebug = (piece, index) => {
          if (selected !== index) return null;
          
          const bbox = rotatedBBoxFromPiece(piece, getPiecePoints, getRotationCenter, rotatePoints, getTransformedPoints);
          
          return (
            <rect
              x={bbox.x}
              y={bbox.y}
              width={bbox.width}
              height={bbox.height}
              fill="none"
              stroke="blue"
              strokeWidth="1"
              strokeDasharray="3,3"
              opacity="0.6"
            />
          );
  };

  // Wheel handler corrigido para evitar warning de passive listener
  // Zoom com scroll do mouse (cursor-centered). Segurando Shift, mantém atalho antigo de rotação.
  const handleWheel = useCallback((e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    // Novo comportamento: se existir seleção (única peça) e NÃO estiver segurando Ctrl, roda a peça.
    if (selected !== null && !e.ctrlKey) {
      const dir = e.deltaY > 0 ? 1 : -1; // scroll para baixo -> sentido horário
      const base = Number(rotationStep) || 1;
      const step = base; // passo configurável
      console.log('🔄 Rotacionando peça', selected, 'em', dir * step, 'graus');
      rotatePiece(selected, dir * step);
      return; // não faz zoom
    }
    // Se múltiplas peças selecionadas (seleção múltipla), continuar usando SHIFT para rotação rápida; caso contrário zoom.
    if (e.shiftKey && selected !== null) {
      const dir = e.deltaY > 0 ? 1 : -1;
      const base = Number(rotationStep) || 1;
      const step = Math.max(5, base * 5); // rotação maior com Shift
      rotatePiece(selected, dir * step);
      return;
    }
    const svg = svgRef.current; if (!svg) return;
    // Converter posição do cursor para coords do SVG
    const pt = svg.createSVGPoint(); pt.x = e.clientX; pt.y = e.clientY;
    const ctm = svg.getScreenCTM(); const p = ctm ? pt.matrixTransform(ctm.inverse()) : { x: 0, y: 0 };
  const vb = parseViewBox(viewBox);
    // fator de zoom
    const zoomFactor = e.deltaY > 0 ? 1.1 : 0.9;
    const newW = Math.max(10, vb.w * zoomFactor);
    const newH = Math.max(10, vb.h * zoomFactor);
    // manter o ponto do cursor fixo, ajustando x/y do viewBox
    const scaleX = (p.x - vb.x) / vb.w;
    const scaleY = (p.y - vb.y) / vb.h;
    const newX = p.x - scaleX * newW;
    const newY = p.y - scaleY * newH;
    setViewBox(`${newX} ${newY} ${newW} ${newH}`);
  }, [viewBox, rotatePiece, selected, parseViewBox, rotationStep]);
  const duplicatePiece = (index) => {
    // Refatorado: usar valores atuais (peças) para calcular e aplicar offsets menores primeiro,
    // evitando colocar a duplicata exatamente em cima de outra peça e facilitando arraste imediato.
    const base = pieces[index];
    if (!base) return;
    const copy = JSON.parse(JSON.stringify(base));
    const spacingPx = getSpacingPx();
    // passo pequeno para duplicação (priorizar offsets próximos)
    const smallStep = Math.max(4, Math.round((spacingPx || 10) / 4));
    const offsets = [
      [smallStep, 0], [-smallStep, 0], [0, smallStep], [0, -smallStep],
      [smallStep, smallStep], [-smallStep, smallStep], [smallStep, -smallStep], [-smallStep, -smallStep],
      // maiores (fallback)
      [spacingPx, 0], [-spacingPx, 0], [0, spacingPx], [0, -spacingPx]
    ];

    const makeCandidate = (ox, oy) => {
      let c = JSON.parse(JSON.stringify(copy));
      if (c.points && c.points.length) {
        c.points = c.points.map(pt => Array.isArray(pt) ? [pt[0] + ox, pt[1] + oy] : { x: pt.x + ox, y: pt.y + oy });
        const bb = bboxOf({ points: c.points }); c.x = bb.x; c.y = bb.y; c.width = bb.width; c.height = bb.height;
      } else if (c.center && c.radius != null) {
        c.center = [ (c.center[0] || 0) + ox, (c.center[1] || 0) + oy ];
        c.x = c.center[0] - c.radius; c.y = c.center[1] - c.radius;
      } else {
        c.x = (c.x || 0) + ox; c.y = (c.y || 0) + oy;
      }
      c = clampPieceToMaterial(c);
      return c;
    };

    // Tentar encontrar um lugar próximo sem colisão
    let chosen = null;
    for (let k = 0; k < offsets.length; k++) {
      const [ox, oy] = offsets[k];
      const cand = makeCandidate(ox, oy);
      const hasCollision = pieces.some((other, idx) => idx !== index && detectCollisionWithExactPoints(cand, other, spacingPx));
      if (!hasCollision) { chosen = cand; break; }
    }

    // Se não encontrou, aplicar um pequeno jitter (6px) para facilitar seleção/arraste
    if (!chosen) {
      const jitter = 6;
      chosen = makeCandidate(jitter, jitter);
      // tentar reduzir colisão movendo um pouco para fora se necessário
      if (pieces.some(other => detectCollisionWithExactPoints(chosen, other, spacingPx))) {
        chosen = clampPieceSpacing(chosen, pieces.length, pieces);
      }
    }

    const newIdx = pieces.length;
    setPieces(prev => [...prev, chosen]);
    setSelected(newIdx);
    setSelectedPieces(new Set([newIdx]));
    setDragging(null);
  };

  // Duplicar TODAS as peças selecionadas preservando as distâncias relativas e manter selecionadas
  const duplicateSelectedPieces = useCallback(() => {
    if (!selectedPieces || selectedPieces.size === 0) return;
    // Capturar estado atual uma vez
    const indices = Array.from(selectedPieces).filter(i => pieces[i] != null);
    if (!indices.length) return;

    const originals = indices.map(i => pieces[i]);
    const inner = getInnerBounds();
    const spacingPxRaw = getSpacingPx();
    // usar passo menor para tentar posicionar o grupo próximo ao original (evitar sobreposição exata)
    const step = Math.max(4, Math.round((spacingPxRaw || 20) / 4));

    // Helpers locais
    const moveGroupBy = (arr, dx, dy) => arr.map(p => deltaMovePiece({ ...p }, dx, dy));
    const groupBounds = (arr) => {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const p of arr) {
        const r = getRealDimensions(p);
        minX = Math.min(minX, r.minX);
        minY = Math.min(minY, r.minY);
        maxX = Math.max(maxX, r.maxX);
        maxY = Math.max(maxY, r.maxY);
      }
      return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
    };
    const clampGroupToInner = (arr) => {
      const gb = groupBounds(arr);
      let dx = 0, dy = 0;
      if (gb.minX < inner.xMin) dx += (inner.xMin - gb.minX);
      if (gb.maxX > inner.xMax) dx += (inner.xMax - gb.maxX);
      if (gb.minY < inner.yMin) dy += (inner.yMin - gb.minY);
      if (gb.maxY > inner.yMax) dy += (inner.yMax - gb.maxY);
      if (dx !== 0 || dy !== 0) return moveGroupBy(arr, dx, dy);
      return arr;
    };
    const collidesGroup = (arr, external) => {
      const s = getSpacingPx();
      for (const p of arr) {
        for (let j = 0; j < external.length; j++) {
          const other = external[j];
          // Não precisa excluir originais explicitamente, colisão com eles também deve ser evitada
          if (detectCollisionWithExactPoints(p, other, s)) return true;
        }
      }
      return false;
    };

    // Gerar offsets em "espiral" para encontrar uma posição livre para o grupo
    const generateOffsets = (maxSteps = 10) => {
      const offs = [];
      for (let k = 1; k <= maxSteps; k++) {
        offs.push([k * step, 0]);
        offs.push([0, k * step]);
        offs.push([k * step, k * step]);
        offs.push([-k * step, 0]);
        offs.push([0, -k * step]);
        offs.push([-k * step, k * step]);
        offs.push([k * step, -k * step]);
        offs.push([-k * step, -k * step]);
      }
      return offs;
    };

    // Base do offset: usar o menor minX/minY do grupo para calcular deslocamento a partir dele
    // eslint-disable-next-line no-unused-vars
    const baseBounds = groupBounds(originals);

    const tryPlaceGroup = () => {
      const external = pieces; // considerar todas as peças existentes
      const offsets = generateOffsets(14);
      for (const [ox, oy] of offsets) {
        // mover o grupo como unidade: deslocar por (ox, oy)
        let copies = moveGroupBy(originals, ox, oy);
        // manter grupo dentro das margens
        copies = clampGroupToInner(copies);
        if (!collidesGroup(copies, external)) {
          return copies;
        }
      }
      // Se nenhuma posição encontrada, última alternativa: deslocar por 2*step em diagonal e clamping
      let fallback = moveGroupBy(originals, 2 * step, 2 * step);
      fallback = clampGroupToInner(fallback);
      return fallback;
    };

    const copies = tryPlaceGroup();
    // Adicionar e selecionar os novos índices
    const startIdx = pieces.length;
    const newPieces = [...pieces, ...copies];
    setPieces(newPieces);
    const newIdxs = new Set(copies.map((_, i) => startIdx + i));
    setSelectedPieces(newIdxs);
    setSelected(startIdx); // focar no primeiro duplicado
    setDragging(null);
  }, [selectedPieces, pieces, getInnerBounds, getSpacingPx, deltaMovePiece, getRealDimensions, detectCollisionWithExactPoints]);

  // Remover a peça selecionada
  const removeSelectedPiece = useCallback(() => {
    // Se houver seleção múltipla, remove todas; senão remove a única selecionada
    setPieces(prev => {
      if (selectedPieces && selectedPieces.size > 0) {
        return prev.filter((_, i) => !selectedPieces.has(i));
      }
      if (selected !== null) {
        return prev.filter((_, i) => i !== selected);
      }
      return prev;
    });
    // limpar estados relacionados
    setSelected(null);
    setDragging(null);
    setFileiraVertical([]);
    setSelectedPieces(new Set());
  }, [selected, selectedPieces]);

  // Atalho de teclado: Delete para remover selecionadas
  useEffect(() => {
    const onKeyDown = (e) => {
      const tag = (e.target && e.target.tagName) ? e.target.tagName.toUpperCase() : '';
      const isEditable = (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (e.target && e.target.isContentEditable));
      if (isEditable) return;
      if (e.key !== 'Delete') return;
      try { e.preventDefault(); } catch {}
      removeSelectedPiece();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [removeSelectedPiece]);

  // Selecionar todas as peças
  const selecionarTudo = useCallback(() => {
    setSelectedPieces(new Set(pieces.map((_, i) => i)));
    setSelected(null);
    setDragging(null);
  }, [pieces]);

  // Atalho de teclado: Ctrl+A para selecionar todas as peças
  useEffect(() => {
    const onKeyDown = (e) => {
      const tag = (e.target && e.target.tagName) ? e.target.tagName.toUpperCase() : '';
      const isEditable = (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (e.target && e.target.isContentEditable));
      if (isEditable) return;
      const isSelectAll = (e.ctrlKey || e.metaKey) && (e.key === 'a' || e.key === 'A');
      if (!isSelectAll) return;
      try { e.preventDefault(); } catch {}
      selecionarTudo();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selecionarTudo]);

  // Simple grid auto-layout (Comelz-like) across inner bounds using first piece size
  const encaixeAutomatico = useCallback(() => {
    if (!pieces.length) return;
    const inner = getInnerBounds();
    const spacingPx = getSpacingPx();

    const firstDims = getRealDimensions(pieces[0]);
    const tileH = firstDims.height;

    let x = inner.xMin;
    let y = inner.yMin;
    const nextPieces = [];

    for (let i = 0; i < pieces.length; i++) {
      const piece = pieces[i];
      const dims = getRealDimensions(piece);

      if (x + dims.width > inner.xMax) {
        x = inner.xMin;
        y += tileH + spacingPx;
      }

      if (y + dims.height > inner.yMax) break;

      let placed = movePieceTo(piece, x, y);
      placed = clampPieceToMaterial(placed);
      nextPieces.push(placed);

  x += dims.width + spacingPx;
    }

    setPieces(nextPieces);
  }, [pieces, getInnerBounds, getSpacingPx, getRealDimensions, movePieceTo, clampPieceToMaterial]);

  // eslint-disable-next-line no-unused-vars
  const simularLayout = async () => {
    try {
      const body = { config, pecas: pieces.map(p => ({ pontos: p.points || p.control_points || [], area: p.area || 0, perimetro: p.perimeter || 0 })) };
      const JAVA_API_BASE = URLManager.getBaseURL();
      const simularRes = await fetch(`${JAVA_API_BASE}/api/layout/simular`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!simularRes.ok) throw new Error('Erro simulação');
      const json = await simularRes.json();
      setLayoutResult(json);
      if (typeof onConsumoCalculado === 'function') {
        let consumoValor = json?.consumo_min_rotated_rect ?? json?.consumo ?? consumoCalculado?.consumo_min_rotated_rect ?? consumoCalculado?.consumo ?? null;
        let consumoFonte = null;
        if (consumoValor != null) {
          consumoFonte = 'backend-layout';
        } else if (pecasDisponiveis && Number.isInteger(indexAtual) && pecasDisponiveis[indexAtual] && pecasDisponiveis[indexAtual].area) {
          consumoValor = pecasDisponiveis[indexAtual].area;
          consumoFonte = 'pecasDisponiveis[indexAtual].area';
        }

        const jsonWithContext = { ...(json || {}), numeroGrade: numeroGrade || null, componente: componente || null };
        if (consumoValor != null) {
          const numeric = typeof consumoValor === 'string' ? Number(consumoValor) : consumoValor;
          jsonWithContext.consumo = numeric;
          jsonWithContext.consumo_min_rotated_rect = numeric;
          console.debug('[EncaixeManualDXF] simularLayout - fornecendo consumo derivado', { consumo: numeric, fonte: consumoFonte });
        } else {
          console.warn('[EncaixeManualDXF] simularLayout - não encontrou consumo numérico; enviando contexto sem consumo', { json });
        }

        onConsumoCalculado(jsonWithContext);
      }
      if (json.pecasPosicionadas && Array.isArray(json.pecasPosicionadas)) {
        const positioned = json.pecasPosicionadas.map((pp, i) => clampPieceToMaterial({ ...pieces[i], x: pp.x || pieces[i].x, y: pp.y || pieces[i].y, rotation: pp.rotation || pieces[i].rotation }));
        setPieces(positioned);
      }
    } catch (err) {
      console.error('Erro ao simular layout', err);
      alert('Erro ao simular layout');
    }
  };

  const unitsPorCamada = layoutResult?.unitsPorCamada ?? pieces.length;
  const totalPecas = layoutResult?.totalPecas ?? pieces.length * (config.camadas || 1);
  const areaBruta = ((config.comprimentoMaterial || 0) / 1000) * ((config.larguraMaterial || 0) / 1000) * (config.camadas || 1);
  const areaLiquida = pieces.reduce((s, p) => s + (p.area || 0), 0) / 1000000;
  const eficiencia = areaBruta ? ((areaLiquida / areaBruta) * 100).toFixed(2) : 0;


  const renderPieceShape = (p, i) => {
    const isSelected = selected === i || selectedPieces.has(i);
    const common = { cursor: repetMode ? 'crosshair' : 'grab', onMouseDown: (e) => {
      // Middle mouse should initiate pan at SVG, not drag pieces
      if (e.button === 1) return; 
      // In Repet mode, let the event bubble to SVG to start drawing the rectangle
      if (repetMode) return;
      startDrag(e, i);
    } };

    const multiSelect = selectedPieces.size > 1 && selectedPieces.has(i);
    const fillColor = multiSelect ? 'rgba(255,87,34,0.85)' : (isSelected ? 'rgba(255,152,0,0.85)' : 'rgba(33,150,243,0.75)');
    const strokeColor = multiSelect ? '#ff5722' : (isSelected ? '#ff9800' : '#1e5799');

    // Se a peça tem entities (grupo de múltiplas entidades)
    if (p.entities && Array.isArray(p.entities) && p.entities.length > 0) {
      // Calcular centro de rotação baseado nas coordenadas LOCAIS dos entities (sem offset da peça)
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      
      p.entities.forEach(entity => {
        if (entity.points && entity.points.length) {
          entity.points.forEach(pt => {
            const x = Array.isArray(pt) ? pt[0] : (pt.x ?? pt[0]);
            const y = Array.isArray(pt) ? pt[1] : (pt.y ?? pt[1]);
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x > maxX) maxX = x;
            if (y > maxY) maxY = y;
          });
        }
      });
      
      const localWidth = maxX - minX;
      const localHeight = maxY - minY;
      const centerX = minX + localWidth / 2;
      const centerY = minY + localHeight / 2;
      
      return (
        <g 
          key={i} 
          data-piece="1"
          transform={`translate(${p.x || 0},${p.y || 0}) translate(${centerX},${centerY}) rotate(${p.rotation || 0}) translate(${-centerX},${-centerY})`}
        >
          {p.entities.map((entity, entityIdx) => {
            const renderPoints = getRenderPoints(entity);
            if (!renderPoints.length) return null;

            const ptsStr = renderPoints.map(([x, y]) => `${x},${y}`).join(' ');
            const isClosed = renderPoints.length >= 3;

            if (entity.type === 'CIRCLE' || entity.type === 'ELLIPSE' || entity.type === 'ARC') {
              return (
                <polygon 
                  key={entityIdx}
                  data-piece="1" 
                  points={ptsStr} 
                  fill={fillColor} 
                  stroke="#222" 
                  strokeWidth={1} 
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  shapeRendering="geometricPrecision"
                  {...common} 
                />
              );
            }
            if (entity.type === 'LWPOLYLINE' || entity.type === 'POLYLINE' || entity.type === 'POLYGON' || entity.type === 'SPLINE' || entity.type === 'RECTANGLE') {
              return isClosed ? (
                <polygon 
                  key={entityIdx}
                  data-piece="1" 
                  points={ptsStr} 
                  fill={fillColor} 
                  stroke="#222" 
                  strokeWidth={1}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  shapeRendering="geometricPrecision"
                  {...common} 
                  style={{ pointerEvents: 'all' }} 
                />
              ) : (
                <g key={entityIdx}>
                  {/* Invisible wide stroke for easier selection on open shapes */}
                  <polyline
                    data-piece="1"
                    points={ptsStr}
                    fill="none"
                    stroke="transparent"
                    strokeWidth={10}
                    style={{ pointerEvents: 'stroke' }}
                    {...common}
                  />
                  {/* Visible stroke */}
                  <polyline 
                    data-piece="1" 
                    points={ptsStr} 
                    fill="none" 
                    stroke={strokeColor} 
                    strokeWidth={1.2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    shapeRendering="geometricPrecision"
                    style={{ pointerEvents: 'none' }} 
                  />
                </g>
              );
            }

            // Generic fallback
            return isClosed ? (
              <polygon 
                key={entityIdx}
                data-piece="1" 
                points={ptsStr} 
                fill={fillColor} 
                stroke="#222" 
                strokeWidth={1}
                strokeLinecap="round"
                strokeLinejoin="round"
                shapeRendering="geometricPrecision"
                {...common} 
              />
            ) : (
              <polyline 
                key={entityIdx}
                data-piece="1" 
                points={ptsStr} 
                fill="none" 
                stroke={strokeColor} 
                strokeWidth={1.2}
                strokeLinecap="round"
                strokeLinejoin="round"
                shapeRendering="geometricPrecision"
                {...common} 
              />
            );
          })}
        </g>
      );
    }

    // Fallback para peças antigas (sem array entities) - compatibilidade
    const renderPoints = getRenderPoints(p);
    if (!renderPoints.length) return null;

    const ptsStr = renderPoints.map(([x, y]) => `${x},${y}`).join(' ');
    const isClosed = renderPoints.length >= 3;

    if (p.type === 'CIRCLE' || p.type === 'ELLIPSE' || p.type === 'ARC') {
      return (
        <g key={i} data-piece="1">
          <polygon data-piece="1" points={ptsStr} fill={fillColor} stroke="#222" strokeWidth={1} {...common} />
        </g>
      );
    }

    if (p.type === 'LWPOLYLINE' || p.type === 'POLYLINE' || p.type === 'POLYGON' || p.type === 'SPLINE' || p.type === 'RECTANGLE') {
      return (
        <g key={i} data-piece="1">
          {isClosed ? (
            <polygon data-piece="1" points={ptsStr} fill={fillColor} stroke="#222" strokeWidth={1} {...common} style={{ pointerEvents: 'all' }} />
          ) : (
            <>
              {/* Invisible wide stroke for easier selection on open shapes */}
              <polyline
                data-piece="1"
                points={ptsStr}
                fill="none"
                stroke="transparent"
                strokeWidth={10}
                style={{ pointerEvents: 'stroke' }}
                {...common}
              />
              {/* Visible stroke */}
              <polyline data-piece="1" points={ptsStr} fill="none" stroke={strokeColor} strokeWidth={1.2} style={{ pointerEvents: 'none' }} />
            </>
          )}
        </g>
      );
    }

    // Generic fallback
    if (renderPoints.length) {
      return (
        <g key={i} data-piece="1">
          {isClosed ? (
            <polygon data-piece="1" points={ptsStr} fill={fillColor} stroke="#222" strokeWidth={1} {...common} />
          ) : (
            <polyline data-piece="1" points={ptsStr} fill="none" stroke={strokeColor} strokeWidth={1.2} {...common} />
          )}
        </g>
      );
    }

    return null;
  };

  // ====== Repet anchor helpers ======
  // findPieceIndexAtPoint, computeAnchorOnPiece e anchorPointOnPiece foram convertidas para useCallback acima

  // Middle-button panning (pressionar botão do scroll e arrastar)
  const onWindowMouseMovePan = useCallback((e) => {
    if (!isPanning) return;
    const { startX, startY, vb } = panRef.current;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    // delta deve ser convertido para coords do viewBox: assumimos 1px de tela ~ vb.w / svgWidth
    const svg = svgRef.current; if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const kx = vb.w / Math.max(1, rect.width);
    const ky = vb.h / Math.max(1, rect.height);
    const newX = vb.x - dx * kx;
    const newY = vb.y - dy * ky;
    setViewBox(`${newX} ${newY} ${vb.w} ${vb.h}`);
  }, [isPanning]);
  const onWindowMouseUpPan = useCallback(() => {
    if (isPanning) setIsPanning(false);
  }, [isPanning]);

  // Attach global listeners during panning
  useEffect(() => {
    if (!isPanning) return;
    const mm = (e) => onWindowMouseMovePan(e);
    const mu = (e) => onWindowMouseUpPan(e);
    window.addEventListener('mousemove', mm);
    window.addEventListener('mouseup', mu);
    return () => {
      window.removeEventListener('mousemove', mm);
      window.removeEventListener('mouseup', mu);
    };
  }, [isPanning, onWindowMouseMovePan, onWindowMouseUpPan]);

  const zoomBy = (factor) => {
  const vb = parseViewBox(viewBox);
    const cx = vb.x + vb.w / 2;
    const cy = vb.y + vb.h / 2;
    const newW = Math.max(10, vb.w * factor);
    const newH = Math.max(10, vb.h * factor);
    setViewBox(`${cx - newW / 2} ${cy - newH / 2} ${newW} ${newH}`);
  };

  const fitToContent = () => {
    if (pieces && pieces.length) {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      pieces.forEach(p => {
        const bb = bboxOf(p);
        minX = Math.min(minX, bb.x);
        minY = Math.min(minY, bb.y);
        maxX = Math.max(maxX, bb.x + bb.width);
        maxY = Math.max(maxY, bb.y + bb.height);
      });
      if (minX === Infinity) return;
      const margin = 40;
      setViewBox(`${Math.floor(minX - margin)} ${Math.floor(minY - margin)} ${Math.ceil(maxX - minX + margin * 2)} ${Math.ceil(maxY - minY + margin * 2)}`);
      return;
    }
    // fallback to material area
    const mw = config.larguraMaterial * svgScale;
    const mh = config.comprimentoMaterial * svgScale;
    setViewBox(`0 0 ${mw} ${mh}`);
  };

  // Indicador visual do espaçamento entre peças (corrigido para múltiplas selecionadas)
  const renderSpacingVisualization = (piece, index) => {
    if (selectedPieces.size > 1) {
      if (!selectedPieces.has(index)) return null;
    } else {
      if (selected !== index) return null;
    }
    const points = getRenderPoints(piece);
    const spacingPx = getSpacingPx();
    if (!points.length || !spacingPx) return null;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    if (piece.entities && Array.isArray(piece.entities)) {
      piece.entities.forEach(entity => {
        if (entity.points && entity.points.length) {
          entity.points.forEach(pt => {
            const x = Array.isArray(pt) ? pt[0] : (pt.x ?? pt[0]);
            const y = Array.isArray(pt) ? pt[1] : (pt.y ?? pt[1]);
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x > maxX) maxX = x;
            if (y > maxY) maxY = y;
          });
        }
      });
    }
    const localWidth = maxX - minX;
    const localHeight = maxY - minY;
    const centerX = minX + localWidth / 2;
    const centerY = minY + localHeight / 2;
    let closedPoints = points;
    if (points.length > 2) {
      const [fx, fy] = points[0];
      const [lx, ly] = points[points.length - 1];
      if (fx !== lx || fy !== ly) {
        closedPoints = [...points, points[0]];
      }
    }
    return (
      <g transform={`translate(${piece.x || 0},${piece.y || 0}) translate(${centerX},${centerY}) rotate(${piece.rotation || 0}) translate(${-centerX},${-centerY})`}>
        <polygon
          points={closedPoints.map(([x, y]) => `${x},${y}`).join(' ')}
          fill="none"
          stroke="rgba(255, 165, 0, 0.35)"
          strokeWidth={Math.max(1, spacingPx * 2)}
          strokeLinejoin="round"
          pointerEvents="none"
        />
      </g>
    );
  };

  // Visualizar a área interna (margens) no SVG
  const renderMarginBounds = () => {
    const inner = getInnerBounds();
    return (
      <rect
        x={inner.xMin}
        y={inner.yMin}
        width={inner.xMax - inner.xMin}
        height={inner.yMax - inner.yMin}
        fill="none"
        stroke="rgba(10, 227, 10, 0.95)"
        strokeWidth={3}
        strokeDasharray="5,5"
        style={{ pointerEvents: 'none' }}
      />
    );
  };

  // === Movimento por setas do teclado ===
// (deve ser declarado após o useState selected)

// ...existing code...

// (adicione este bloco após a linha do useState selected)

useEffect(() => {
  const handleArrowMove = (e) => {
    const tag = (e.target && e.target.tagName) ? e.target.tagName.toUpperCase() : '';
    const isEditable = (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (e.target && e.target.isContentEditable));
    if (isEditable) return;
    const arrowKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
    if (!arrowKeys.includes(e.key)) return;
    if (selectedPieces.size === 0 && selected == null) return;
    e.preventDefault();
    // Allow holding Alt to temporarily remove spacing when moving with arrows
    try { spacingOverrideRef.current = e.altKey ? 0 : null; } catch (err) { spacingOverrideRef.current = null; }
    const step = e.shiftKey ? 10 : 1;
    let dx = 0, dy = 0;
    if (e.key === 'ArrowUp') dy = -step;
    if (e.key === 'ArrowDown') dy = step;
    if (e.key === 'ArrowLeft') dx = -step;
    if (e.key === 'ArrowRight') dx = step;
    setPieces(prev => {
      const groupSet = selectedPieces.size > 0 ? new Set(selectedPieces) : (selected != null ? new Set([selected]) : new Set());
      let moved = prev.map((p, i) => groupSet.has(i) ? deltaMovePiece(p, dx, dy) : p);
      const inner = getInnerBounds();
      let gMinX = Infinity, gMinY = Infinity, gMaxX = -Infinity, gMaxY = -Infinity;
      moved.forEach((p, i) => {
        if (!groupSet.has(i)) return;
        const r = getRealDimensions(p);
        gMinX = Math.min(gMinX, r.minX);
        gMinY = Math.min(gMinY, r.minY);
        gMaxX = Math.max(gMaxX, r.maxX);
        gMaxY = Math.max(gMaxY, r.maxY);
      });
      let corrX = 0, corrY = 0;
      if (gMinX < inner.xMin) corrX += (inner.xMin - gMinX);
      if (gMaxX > inner.xMax) corrX += (inner.xMax - gMaxX);
      if (gMinY < inner.yMin) corrY += (inner.yMin - gMinY);
      if (gMaxY > inner.yMax) corrY += (inner.yMax - gMaxY);
      if (corrX !== 0 || corrY !== 0) {
        moved = moved.map((p, idx) => groupSet.has(idx) ? deltaMovePiece(p, corrX, corrY) : p);
      }
      const external = prev.filter((_, idx) => !groupSet.has(idx));
      const spacingPx = getSpacingPx();
      const anyCollisionBetween = (candArr, extArr) => {
        for (const cp of candArr) {
          for (const ep of extArr) {
            if (detectCollisionWithExactPoints(cp, ep, spacingPx)) return true;
          }
        }
        return false;
      };
      let attempts = 0, maxAttempts = 20;
      while (anyCollisionBetween(moved.filter((_,i)=>groupSet.has(i)), external) && attempts < maxAttempts) {
        moved = moved.map((p, idx) => groupSet.has(idx) ? deltaMovePiece(p, -dx, -dy) : p);
        attempts++;
      }
      return moved;
    });
    // reset override after applying movement
    try { spacingOverrideRef.current = null; } catch (err) { spacingOverrideRef.current = null; }
  };
  window.addEventListener('keydown', handleArrowMove);
  return () => window.removeEventListener('keydown', handleArrowMove);
}, [selected, selectedPieces, deltaMovePiece, getInnerBounds, getRealDimensions, getSpacingPx, detectCollisionWithExactPoints]);

  // Indicadores visuais de snap nas margens (feedback)
        // eslint-disable-next-line no-unused-vars
        const renderCollisionPoints = (piece, index) => {
          if (selected !== index) return null;
          const points = getRenderPoints(piece);
          return (
            <g>
              {points.map(([x, y], i) => (
                <circle 
                  key={i} 
                  cx={x} 
                  cy={y} 
                  r={2} 
                  fill="red" 
                  opacity="0.8"
                  stroke="darkred"
                  strokeWidth="1"
                />
              ))}
              <polyline 
                points={points.map(([x, y]) => `${x},${y}`).join(' ')} 
                fill="none" 
                stroke="red" 
                opacity="0.4"
                strokeWidth="1"
                strokeDasharray="2,2"
              />
            </g>
          );
        };

  // ...função removida, versão única mantida acima...
  

  // Keep the material area centered in the view when there are no pieces
  useEffect(() => {
    // If there are pieces, do not override their viewBox
    if (pieces && pieces.length) return;
    const mw = (config.larguraMaterial || DEFAULT_CONFIG.larguraMaterial) * svgScale;
    const mh = (config.comprimentoMaterial || DEFAULT_CONFIG.comprimentoMaterial) * svgScale;
    // set viewBox exactly to material area so preserveAspectRatio centers it
    setViewBox(`0 0 ${mw} ${mh}`);
  }, [pieces, config.comprimentoMaterial, config.larguraMaterial, svgScale]);

  // Close handler that also optionally notifies parent
  const handleClose = () => {
    if (typeof onClose === 'function') onClose();
  };

  // Limpa seleção ao clicar fora de qualquer peça (em qualquer elemento não marcado como peça)
  const handleSvgClick = useCallback((e) => {
    // No modo Repet, não limpa seleções (permite desenhar retângulo)
    if (repetMode) return;
    
    // Se acabamos de finalizar uma seleção por arrasto, não limpa
    if (justFinishedSelection) return;
    
    const t = e.target;
    const isOnPiece = t && typeof t.closest === 'function' && t.closest('[data-piece="1"]');
    if (!isOnPiece) {
      setSelected(null);
      setSelectedPieces(new Set());
    }
  }, [repetMode, justFinishedSelection]);

  // Overlay de carregamento durante upload/processamento do DXF (escopo do componente)
  const LoadingOverlay = () => (
    uploading ? (
      <Box sx={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(255,255,255,0.6)',
        zIndex: 2000
      }}>
        <LoadingSpinner message="Processando DXF... Acordando servidor Python (1ª vez pode demorar)" />
      </Box>
    ) : null
  );

  return (
    <div className="encaixe-modal-backdrop">
      <div className={`encaixe-modal ${compactUI ? 'compact' : ''}`} style={{ maxHeight: '95vh', height: '95vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} tabIndex={0}>
        <LoadingOverlay />
        <div className="encaixe-header">
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <strong>Encaixe Manual / Visualizador DXF</strong>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={() => setCompactUI(v => !v)} title={compactUI ? 'Expandir campos' : 'Compactar campos'}>
              {compactUI ? 'Compacto' : 'Normal'}
            </button>
            <button onClick={() => setShowThumbnails(v => !v)} title={showThumbnails ? 'Ocultar miniaturas' : 'Mostrar miniaturas'}>
              {showThumbnails ? 'Miniaturas: On' : 'Miniaturas: Off'}
            </button>
            <button onClick={() => setShowConfig(v => !v)} title={showConfig ? 'Ocultar painel esquerdo' : 'Mostrar painel esquerdo'}>
              {showConfig ? 'Ocultar Painel' : 'Mostrar Painel'}
            </button>
            <button onClick={() => zoomBy(0.8)} title="Zoom in">+</button>
            <button onClick={() => zoomBy(1.25)} title="Zoom out">-</button>
            <button onClick={fitToContent} title="Ajustar tudo">Ajustar</button>
            <button
              onClick={(e) => {
                const axis = e.shiftKey ? 'vertical' : 'horizontal';
                if (selected !== null) {
                  mirrorPiece(selected, axis);
                } else if (selectedPieces.size > 0) {
                  const firstSelected = Array.from(selectedPieces)[0];
                  mirrorPiece(firstSelected, axis);
                }
              }}
              disabled={selected === null && selectedPieces.size === 0}
              title="Espelhar peça (Shift = vertical)"
            >Espelhar</button>
            <button
              onClick={() => {
                if (selectedPieces && selectedPieces.size > 1) {
                  alignSelectedPieces(selectedPieces);
                } else if (selected !== null) {
                  alignPiece(selected);
                } else if (selectedPieces.size === 1) {
                  const firstSelected = Array.from(selectedPieces)[0];
                  alignPiece(firstSelected);
                }
              }}
              disabled={selected === null && selectedPieces.size === 0}
              title="Alinhar peça automaticamente (corrige rotação indesejada)"
              style={{ backgroundColor: '#4CAF50', color: 'white', fontWeight: 'bold' }}
            >Alinhar</button>
            <button onClick={handleClose} title="Fechar">Fechar</button>
          </div>
        </div>

        {/* Barra de miniaturas das peças do DXF múltiplo - Novo componente (colapsável) */}
        {showThumbnails && (
          <ThumbnailBar
            pecas={pecasDisponiveis}
            selectedIndex={indexAtual}
            onSelect={(index) => {
              setIndexAtual(index);
              if (onPecaSelect) {
                onPecaSelect(index);
              }
              if (pecasDisponiveis[index]) {
                carregarPecaNoCanvas(pecasDisponiveis[index]);
              }
            }}
          />
        )}

  <div className="encaixe-body" style={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
          {showConfig && (
          <div className={`config-panel ${compactUI ? 'compact' : ''}`} style={{ width: compactUI ? 260 : 340, background: '#fafafa', padding: 12, borderRadius: 8, height: '100%', maxHeight: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', paddingRight: 8, boxSizing: 'border-box' }}>
              <h3>Configurações do Material</h3>
              <label>Comprimento (mm):</label>
              <input type="number" value={config.comprimentoMaterial}
                onChange={e => {
                  const v = e.target.value;
                  setConfig({ ...config, comprimentoMaterial: v === '' ? '' : Number(v) });
                }} />
              <label>Largura (mm):</label>
              <input type="number" value={config.larguraMaterial}
                onChange={e => {
                  const v = e.target.value;
                  setConfig({ ...config, larguraMaterial: v === '' ? '' : Number(v) });
                }} />
              <label>Margem esquerda (mm):</label>
              <input type="number" value={config.margemEsquerda}
                onChange={e => {
                  const v = e.target.value;
                  setConfig({ ...config, margemEsquerda: v === '' ? '' : Number(v) });
                }} />
              <label>Margem direita (mm):</label>
              <input type="number" value={config.margemDireita}
                onChange={e => {
                  const v = e.target.value;
                  setConfig({ ...config, margemDireita: v === '' ? '' : Number(v) });
                }} />
              <label>Margem topo (mm):</label>
              <input type="number" value={config.margemTopo}
                onChange={e => {
                  const v = e.target.value;
                  setConfig({ ...config, margemTopo: v === '' ? '' : Number(v) });
                }} />
              <label>Margem baixo (mm):</label>
              <input type="number" value={config.margemBaixo}
                onChange={e => {
                  const v = e.target.value;
                  setConfig({ ...config, margemBaixo: v === '' ? '' : Number(v) });
                }} />
              <label>Espaçamento entre peças (mm):</label>
              <input 
                type="number" 
                value={config.distanciaPecas} 
                onChange={e => {
                  const v = e.target.value;
                  setConfig({ ...config, distanciaPecas: v === '' ? '' : Number(v) });
                }} 
              />
              <label>Camadas:</label>
              <input type="number" value={config.camadas} onChange={e => {
                const v = e.target.value;
                setConfig({ ...config, camadas: v === '' ? '' : Number(v) });
              }} />
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                <input type="checkbox" checked={!!config.aproximacaoAutomatica} onChange={e => setConfig({ ...config, aproximacaoAutomatica: e.target.checked })} />
                <label> Aproximação Automática (duplicar aproxima mantendo espaçamento)</label>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                <input type="checkbox" checked={!!config.giraAoAproximar} onChange={e => setConfig({ ...config, giraAoAproximar: e.target.checked })} />
                <label> Gira ao Aproximar (tenta reposicionar ao rotacionar para manter espaçamento)</label>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                <input type="checkbox" checked={!!config.permitirSobreposicao} onChange={e => setConfig({ ...config, permitirSobreposicao: e.target.checked })} />
                <label> Permitir Sobreposição ao Girar/Espelhar</label>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                <input type="checkbox" checked={!!config.altaQualidade} onChange={e => setConfig({ ...config, altaQualidade: e.target.checked })} />
                <label> Alta Qualidade (desativa simplificação)</label>
              </div>
              <div style={{ marginTop: 8 }}>
                <input type="file" accept=".dxf" onChange={e => setDxfFile(e.target.files[0])} />
                <button onClick={handleUploadDXF} disabled={uploading || !dxfFile}>{uploading ? 'Enviando...' : 'Carregar DXF'}</button>
              </div>

              <div style={{ marginTop: 12 }}>
                <h4>Preenchimento Automático</h4>

                {/* Botão para selecionar todas as peças */}
                <button
                  onClick={selecionarTudo}
                  disabled={!pieces.length}
                  style={{ marginRight: 8, marginBottom: 8, backgroundColor: '#795548', color: 'white' }}
                >
                  Selecionar Tudo
                </button>

                <div style={{ marginTop: 6, fontSize: 12, color: '#555' }}>
                  {selectedPieces.size >= 1
                    ? `Selecionadas: ${selectedPieces.size} peças`
                    : selected !== null
                    ? `1 peça selecionada`
                    : `Nenhuma peça selecionada`}
                </div>
              </div>

              <div style={{ marginTop: 16 }}>
                <h4>Resultados</h4>
                <div>Peças por camada: {unitsPorCamada}</div>
                <div>Total peças: {totalPecas}</div>
                <div>Área bruta (m²): {areaBruta.toFixed(3)}</div>
                <div>Área líquida (m²): {areaLiquida.toFixed(3)}</div>
                <div>Eficiência: {eficiencia}%</div>
              </div>

                <div style={{ marginTop: 12 }}>
                <h4>Ações peça selecionada</h4>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    onClick={() => {
                      if (selectedPieces && selectedPieces.size > 1) {
                        rotatePiecesInPlace(selectedPieces, 90);
                      } else if (selected !== null) {
                        rotatePiece(selected, 90);
                      } else if (selectedPieces.size > 0) {
                        const firstSelected = Array.from(selectedPieces)[0];
                        rotatePiece(firstSelected, 90);
                      }
                    }}
                    disabled={selected === null && selectedPieces.size === 0}
                  >Girar 90°</button>
                  <button
                    onClick={(e) => {
                      const axis = e.shiftKey ? 'vertical' : 'horizontal';
                      if (selected !== null) {
                        mirrorPiece(selected, axis);
                      } else if (selectedPieces.size > 0) {
                        const firstSelected = Array.from(selectedPieces)[0];
                        mirrorPiece(firstSelected, axis);
                      }
                    }}
                    disabled={selected === null && selectedPieces.size === 0}
                    title="Espelhar peça (Shift = vertical)"
                  >Espelhar</button>
                  <button
                    onClick={() => {
                      if (selectedPieces && selectedPieces.size > 1) {
                        alignSelectedPieces(selectedPieces);
                      } else if (selected !== null) {
                        alignPiece(selected);
                      } else if (selectedPieces.size === 1) {
                        const firstSelected = Array.from(selectedPieces)[0];
                        alignPiece(firstSelected);
                      }
                    }}
                    disabled={selected === null && selectedPieces.size === 0}
                    title="Alinhar peça automaticamente (corrige rotação indesejada)"
                    style={{ backgroundColor: '#4CAF50', color: 'white' }}
                  >Alinhar Peça</button>
                  <button
                    onClick={() => {
                      if (selectedPieces && selectedPieces.size > 0) {
                        duplicateSelectedPieces();
                      } else if (selected !== null) {
                        duplicatePiece(selected);
                      }
                    }}
                    disabled={selected === null && selectedPieces.size === 0}
                    title={selectedPieces.size > 1 ? 'Duplicar grupo selecionado mantendo espaçamento' : 'Duplicar peça'}
                  >Duplicar</button>
                  <button onClick={removeSelectedPiece} disabled={(selected === null) && (selectedPieces.size === 0)}>
                    {selectedPieces.size > 1 ? `Remover (${selectedPieces.size})` : 'Remover'}
                  </button>
                  <button onClick={encaixeAutomatico} disabled={!pieces.length}>Encaixe Automático</button>
                </div>
                {/* Passo de rotação */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                  <label style={{ fontSize: 13, color: '#333' }}>Passo de rotação (°):</label>
                  <input
                    type="number"
                    min="0.1"
                    step="0.1"
                    value={rotationStep}
                    onChange={e => setRotationStep(Number(e.target.value) || 1)}
                    style={{ width: 80, padding: '4px 6px' }}
                    title="Grau por "
                  />
                  <small style={{ color: '#666' }}>(Shift = rápido)</small>
                </div>
                
                {/* Dica de seleção por arrasto */}
                <div style={{ 
                  background: '#e3f2fd', 
                  border: '1px solid #90caf9', 
                  borderRadius: '6px', 
                  padding: '8px', 
                  marginTop: '8px',
                  fontSize: '12px',
                  color: '#1565c0'
                }}>
                  <strong>💡 Dica:</strong> Clique e arraste no desenho para selecionar múltiplas peças!
                  <br/>
                  <small style={{ color: '#1976d2' }}>
                    • Shift: adiciona à seleção | Ctrl: alterna seleção
                  </small>
                </div>
                
                {/* Formulário para cálculo manual, campos exibidos somente se DXF carregado */}
                <div className={`consumo-manual-form ${compactUI ? 'compact' : ''}`} style={{ margin: compactUI ? '10px 0' : '20px 0', padding: compactUI ? '8px' : '12px', border: '1px solid #ccc', borderRadius: '8px', background: '#fafafa' }}>
                  <h3>Calcular Consumo Manual</h3>
                  {/* Sempre mostra os campos. Se não houver DXF carregado nesta tela, exibe aviso */}
                  {!dxfFile && !repetMode && (
                    <div style={{
                      background: '#fff8e1',
                      border: '1px solid #ffecb3',
                      color: '#8d6e63',
                      padding: '8px',
                      borderRadius: 6,
                      fontSize: 12,
                      marginBottom: 10
                    }}>
                      Arquivo DXF não carregado nesta tela. Você já pode ter carregado anteriormente. Para calcular sem re‑upload: selecione peças (ou ative "Desenhar Repet") ou carregue novamente o DXF abaixo.
                    </div>
                  )}
                  {/* Botões do modo Repet e parâmetros */}
                  <>
                      {/* Seção: Retângulo do Repet */}
                      <div style={{ padding: '8px', margin: '6px 0 10px', border: '1px dashed #1976d2', borderRadius: 6, background: '#e3f2fd' }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                          <button
                            type="button"
                            onClick={() => setRepetMode(v => !v)}
                            style={{ background: repetMode ? '#1565c0' : '#1976d2', color: '#fff', border: 'none', padding: '6px 10px', borderRadius: 4 }}
                          >{repetMode ? 'Sair do modo Repet' : 'Desenhar Repet (Retângulo)'}</button>
                          <button
                            type="button"
                            onClick={() => { setRepetRect(null); setRepetStart(null); setIsDrawingRepet(false); }}
                            disabled={!repetRect}
                            style={{ background: '#999', color: '#fff', border: 'none', padding: '6px 10px', borderRadius: 4 }}
                          >Limpar Repet</button>
                        </div>
                        <small style={{ color: '#0d47a1' }}>
                          Dica: com o modo Repet ativo, clique e arraste no desenho (SVG) para criar o retângulo. Ele define largura/altura do layout do repet diretamente no cálculo em m² (override).
                        </small>
                        {repetRect && (
                          <div style={{ marginTop: 6, fontSize: 13 }}>
                            {(() => { const { larguraCm, alturaCm } = getRepetDimsCm(); return (
                              <>
                                <div>Largura (cm): {larguraCm.toFixed(2)}</div>
                                <div>Altura (cm): {alturaCm.toFixed(2)}</div>
                              </>
                            ); })()}
                          </div>
                        )}
                      </div>

                      <select
                        value={tipoCalculo}
                        onChange={e => setTipoCalculo(e.target.value)}
                        name="tipoCalculo"
                        className="form-select"
                        style={{ margin: '6px 0' }}
                      >
                        <option value="metroLinear">Metro Linear</option>
                        <option value="metroQuadrado">Metro Quadrado</option>
                      </select>

                      <input
                        type="number"
                        name="quantidadePares"
                        value={quantidadePares ?? ''}
                        onChange={e => setQuantidadePares(e.target.value)}
                        placeholder="Quantidade de Pares"
                        className="form-control"
                        style={{ height: compactUI ? '26px' : '28px', fontSize: compactUI ? '0.85rem' : '0.95rem', margin: '1px 0' }}
                        autoComplete="off"
                      />
                      <input
                        type="number"
                        name="quantidadePeca"
                        value={quantidadePeca ?? ''}
                        onChange={e => setQuantidadePeca(e.target.value)}
                        placeholder="Quantidade de Peça Por (Pé)"
                        className="form-control"
                        style={{ height: compactUI ? '26px' : '28px', fontSize: compactUI ? '0.85rem' : '0.95rem', margin: '1px 0' }}
                        autoComplete="off"
                      />
                      <input
                        type="number"
                        name="espacamento"
                        value={spacing ?? ''}
                        onChange={e => setSpacing(e.target.value)}
                        placeholder="Espaçamento entre peças (mm)"
                        className="form-control"
                        style={{ height: compactUI ? '26px' : '28px', fontSize: compactUI ? '0.85rem' : '0.95rem', margin: '1px 0' }}
                        min="0"
                        step="0.1"
                        autoComplete="off"
                      />

                      {tipoCalculo === 'metroLinear' && (
                        <>
                          <input
                            type="number"
                            name="larguraMaterial"
                            value={config.larguraMaterial || ''}
                            onChange={e => setConfig({ ...config, larguraMaterial: e.target.value })}
                            placeholder="Largura do Material (mm)"
                            className="form-control"
                            style={{ height: compactUI ? '26px' : '28px', fontSize: compactUI ? '0.85rem' : '0.95rem', margin: '1px 0' }}
                          />
                          <input
                            type="number"
                            name="alturaMaterial"
                            value={config.comprimentoMaterial || ''}
                            onChange={e => setConfig({ ...config, comprimentoMaterial: e.target.value })}
                            placeholder="Altura do Material (mm)"
                            className="form-control"
                            style={{ height: compactUI ? '26px' : '28px', fontSize: compactUI ? '0.85rem' : '0.95rem', margin: '1px 0' }}
                          />
                        </>
                      )}
                      <input
                        type="number"
                        name="percentualPerda"
                        value={percentualPerda ?? ''}
                        onChange={e => setPercentualPerda(e.target.value)}
                        placeholder="Percentual de Perda (%)"
                        className="form-control"
                        style={{ height: compactUI ? '26px' : '28px', fontSize: compactUI ? '0.85rem' : '0.95rem', margin: '1px 0' }}
                        min="0"
                        step="1"
                        autoComplete="off"
                      />
                    </>
                  
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                    <button
                      style={{ padding: '8px 16px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                      onClick={handleCalcularConsumoManual}
                      disabled={!repetMode && !dxfFile && selectedPieces.size === 0}
                    >
                      {repetMode ? 'Calcular Consumo (usar Repet se definido)' : 'Calcular Consumo'}
                    </button>
                  </div>
                  {consumoCalculado && (
                    <div style={{ marginTop: '16px', fontWeight: 'bold', color: '#388e3c' }}>
                      {typeof consumoCalculado === 'object' ? (
                        <pre>{JSON.stringify(consumoCalculado, null, 2)}</pre>
                      ) : (
                        <>Consumo: {consumoCalculado} m²</>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ marginTop: 12 }}>
                <button onClick={onClose}>Fechar</button>
              </div>
            </div>
          </div>
          )}

          <div style={{ flex: 1, minHeight: 0, position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <svg
              ref={svgRef}
              className="encaixe-svg"
              viewBox={viewBox}
              preserveAspectRatio="xMidYMid meet"
              onWheel={handleWheel}
              onClick={handleSvgClick}
              onMouseDown={onSvgMouseDown}
              onMouseMove={onSvgMouseMove}
              onMouseUp={onSvgMouseUp}
              tabIndex={0}
            >
              <rect x={0} y={0} width={config.larguraMaterial * svgScale} height={config.comprimentoMaterial * svgScale} fill="#f8f9fa" stroke="#333" strokeWidth={2} />
              {/* Área útil dentro das margens */}
              {renderMarginBounds()}
              {/* Removido renderSnapIndicators inexistente */}
              {/* Renderização das peças DXF */}
              {pieces.map(renderPieceShape)}
              {/* Retângulo Repet desenhado */}
              {repetRect && (
                (() => {
                  // O ponto inicial do retângulo deve SEMPRE ser exatamente o ponto de snap (centro do círculo)
                  // Adiciona um círculo de debug no ponto inicial do retângulo
                  const x0 = repetRect.x0;
                  const y0 = repetRect.y0;
                  const x1 = repetRect.x1;
                  const y1 = repetRect.y1;
                  const x = Math.min(x0, x1);
                  const y = Math.min(y0, y1);
                  const w = Math.abs(x1 - x0);
                  const h = Math.abs(y1 - y0);
                  const { larguraCm, alturaCm } = getRepetDimsCm();
                  // Debug: log quando renderiza
                  if (w > 0 || h > 0) {
                    console.log('✅ Renderizando retângulo Repet:', { x, y, w, h, larguraCm, alturaCm, x0, y0 });
                  }
                  return (
                    <g style={{ pointerEvents: 'none' }}>
                      {/* Círculo de debug no ponto inicial do retângulo */}
                      <circle
                        cx={x0}
                        cy={y0}
                        r={10}
                        fill="#00e676"
                        fillOpacity={0.7}
                        stroke="#1b5e20"
                        strokeWidth={3}
                        pointerEvents="none"
                      />
                      <rect
                        x={x}
                        y={y}
                        width={w}
                        height={h}
                        fill="rgba(25,118,210,0.15)"
                        stroke={repetMode ? '#1565c0' : '#1976d2'}
                        strokeWidth={2}
                        strokeDasharray={isDrawingRepet ? '4,4' : '6,3'}
                        pointerEvents="none"
                      />
                      <text
                        x={x + w / 2}
                        y={y - 6}
                        fontSize={14}
                        fill="#0d47a1"
                        textAnchor="middle"
                        style={{ userSelect: 'none', pointerEvents: 'none' }}
                      >{larguraCm.toFixed(2)}cm × {alturaCm.toFixed(2)}cm</text>
                    </g>
                  );
                })()
              )}
              
              {/* Retângulo de seleção (drag selection) */}
              {selectionBox && (
                (() => {
                  const x = Math.min(selectionBox.x0, selectionBox.x1);
                  const y = Math.min(selectionBox.y0, selectionBox.y1);
                  const w = Math.abs(selectionBox.x1 - selectionBox.x0);
                  const h = Math.abs(selectionBox.y1 - selectionBox.y0);
                  
                  return (
                    <rect
                      x={x}
                      y={y}
                      width={w}
                      height={h}
                      fill="rgba(76, 175, 80, 0.2)"
                      stroke="#4CAF50"
                      strokeWidth={2}
                      strokeDasharray="5,5"
                      pointerEvents="none"
                    />
                  );
                })()
              )}
              {/* Visualização do espaçamento entre peças (contorno laranja) */}
              {pieces.map((piece, idx) => {
                const el = renderSpacingVisualization(piece, idx);
                if (!el) return null;
                // Clone to enforce pointer-events: none on this overlay
                return React.cloneElement(el, { key: `sv-${idx}`, style: { pointerEvents: 'none' } });
              })}
            {/* Highlight visual da quina mais próxima ao mouse (snap) */}
            {snapHighlight && (
              <circle
                cx={snapHighlight.x}
                cy={snapHighlight.y}
                r={14}
                fill="#ff1744"
                fillOpacity={0.5}
                stroke="#b71c1c"
                strokeWidth={3}
                pointerEvents="none"
              />
            )}
            </svg>
          </div>
        </div>
      </div>
      
      {/* Modal Personalizado de Resultado de Consumo */}
      <Dialog
        open={modalConsumoOpen}
        onClose={() => setModalConsumoOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontWeight: 'bold'
        }}>
          📊 Resultado do Cálculo de Consumo
        </DialogTitle>
        <DialogContent style={{ padding: '24px', marginTop: '16px' }}>
          {modalConsumoData && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{
                padding: '16px',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                borderLeft: '4px solid #667eea'
              }}>
                <Typography variant="subtitle2" color="textSecondary" style={{ marginBottom: '8px' }}>
                  Consumo (paralelograma)
                </Typography>
                <Typography variant="h5" style={{ fontWeight: 'bold', color: '#333' }}>
                  {modalConsumoData.consumo} mm²
                </Typography>
              </div>
              
              <div style={{
                padding: '16px',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                borderLeft: '4px solid #764ba2'
              }}>
                <Typography variant="subtitle2" color="textSecondary" style={{ marginBottom: '8px' }}>
                  Perímetro
                </Typography>
                <Typography variant="h5" style={{ fontWeight: 'bold', color: '#333' }}>
                  {modalConsumoData.perimetro} mm
                </Typography>
              </div>
              
              {modalConsumoData.usandoRepet && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#fff3cd',
                  borderRadius: '8px',
                  border: '1px solid #ffc107',
                  marginTop: '8px'
                }}>
                  <Typography variant="body2" style={{ color: '#856404', fontWeight: '500' }}>
                    ℹ️ Modo Repet: largura/altura override aplicados
                  </Typography>
                </div>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button 
            onClick={() => setModalConsumoOpen(false)}
            variant="contained"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              fontWeight: 'bold',
              padding: '8px 24px'
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default EncaixeManualDXF;
